/**
 * V2EmailComposer — AI EMAIL STUDIO v3 "Aurora Glass".
 * Three floating glass islands over an aurora field. Email = light-paper hero
 * (real dark-brand email renders WYSIWYG inside the paper frame — brand
 * rendering untouched). Gallery cards show mini-WYSIWYG thumbnails; AI
 * suggestions glow. Co-writer: context card, action chips + Undo, tone chips,
 * targeted edits. All gates unchanged (confirm + suppression + warm-up caps).
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { C } from './V2AdminOSPage';
import { Sparkles, Copy, Trash2, ArrowUp, ArrowDown, X } from 'lucide-react';
import { NeonStatus, sendStatus, leadStatus } from './NeonStatus';
import { ensureMotionStyles, runOnce, motionEnabled, setMotionEnabled } from './studioMotion';
import { V2IconPicker } from './V2IconPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'kindra-admin-token';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}`, 'Content-Type': 'application/json' });

// ─── Aurora Glass tokens ─────────────────────────────────────────────────
const BG = '#070313';
const SPEC = 'linear-gradient(95deg,#00E5C3 0%,#38B6FF 25%,#7C5CF6 50%,#C448F0 75%,#FF2E9A 100%)';
const TXT = '#F2F5FF';
const SUB = '#9AA3C7';
const INK = '#1A1440';
const INK_SUB = '#6E769C';
const HEAD = 'Poppins,Inter,sans-serif';
const popover = { position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', width: 'min(540px, 96%)', zIndex: 40, background: 'rgba(10,5,26,0.97)', backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)', border: '1px solid rgba(124,92,246,0.45)', borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.65), 0 0 30px rgba(124,92,246,0.25)', padding: 14, cursor: 'default' };
const glass = (r = 18) => ({ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: r, boxShadow: '0 18px 50px rgba(0,0,0,0.45)' });
const btn = (bg, fg = '#fff') => ({ background: bg, color: fg, border: 'none', borderRadius: 999, padding: '9px 18px', fontFamily: 'Inter', fontWeight: 700, cursor: 'pointer', fontSize: 13 });
const mini = { background: 'transparent', color: TXT, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999, padding: '4px 11px', fontFamily: 'Inter', fontWeight: 600, cursor: 'pointer', fontSize: 11 };
const inputS = { width: '100%', padding: '10px 13px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(7,3,19,0.6)', color: TXT, fontFamily: 'Inter', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const quiet = { background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.12)', color: TXT, fontFamily: 'Inter', fontSize: 12.5, padding: '4px 2px', outline: 'none' };

const post = async (path, body) => {
  const r = await fetch(`${API}${path}`, { method: 'POST', headers: auth(), body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(typeof d.detail === 'string' ? d.detail : (d.detail?.hint || `HTTP ${r.status}`)); e.detail = d.detail; throw e; }
  return d;
};
const get = async (path) => {
  const r = await fetch(`${API}${path}`, { headers: auth() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
};
const patch = async (path, body) => {
  const r = await fetch(`${API}${path}`, { method: 'PATCH', headers: auth(), body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
};

const Aurora = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }} aria-hidden>
    {[['#FF2E9A', 0.35, '72%', '-12%', 520], ['#7C3AED', 0.30, '86%', '38%', 460], ['#00E5C3', 0.26, '-8%', '78%', 520], ['#3B82F6', 0.18, '-6%', '-10%', 440]].map(([c, o, x, y, s], i) => (
      <div key={i} style={{ position: 'absolute', left: x, top: y, width: s, height: s, borderRadius: '50%', background: c, opacity: o, filter: 'blur(130px)' }} />
    ))}
    {[['12%', '30%', 26, 45], ['30%', '72%', 34, -18], ['64%', '14%', 22, 20], ['82%', '66%', 30, 60], ['46%', '86%', 24, -40]].map(([x, y, s, rot], i) => (
      <div key={`s${i}`} style={{ position: 'absolute', left: x, top: y, width: 0, height: 0, borderLeft: `${s / 2}px solid transparent`, borderRight: `${s / 2}px solid transparent`, borderBottom: `${s}px solid rgba(242,245,255,0.05)`, transform: `rotate(${rot}deg)` }} />
    ))}
  </div>
);

// ─── mini-WYSIWYG thumbnails — see it before you drop it in ──────────────
const Thumb = ({ id, group }) => {
  const wrap = (children) => (
    <div style={{ width: 78, height: 58, borderRadius: 10, background: '#FAFBFF', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, overflow: 'hidden', padding: 6, boxSizing: 'border-box' }}>{children}</div>
  );
  const line = (w, h = 3, c = '#C6CBE4') => <div style={{ width: w, height: h, borderRadius: 2, background: c }} />;
  if (id === 'hero_spacious' || id === 'big_statement') return wrap(<>
    <div style={{ width: 22, height: 2.5, borderRadius: 2, background: SPEC }} />
    <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 11, color: '#23264B' }}>{id === 'hero_spacious' ? 'The quiet leaks' : 'One big line'}</span>
    {line(34, 2.5, '#E1E4F2')}</>);
  if (id === 'pull_quote_grand') return wrap(<>
    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 12, fontStyle: 'italic', color: '#23264B' }}>“worth it”</span>
    <span style={{ fontSize: 6.5, fontWeight: 800, letterSpacing: '0.14em', color: '#9AA1C2' }}>CLIENT · BNE</span></>);
  if (id === 'divider_labeled') return wrap(<div style={{ display: 'flex', gap: 4, alignItems: 'center', width: '100%' }}>
    {line(18, 1, '#D5D9EA')}<span style={{ fontSize: 6.5, fontWeight: 800, letterSpacing: '0.12em', color: '#9AA1C2', whiteSpace: 'nowrap' }}>THE NUMBERS</span>{line(18, 1, '#D5D9EA')}</div>);
  if (id === 'at_a_glance') return wrap(<>
    {[0, 1].map((n) => <div key={n} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', borderTop: n ? '1px solid #E7EAF5' : 'none', paddingTop: n ? 3 : 0 }}>
      {line(20, 2.5, '#C6CBE4')}{line(14, 2.5, '#23264B')}</div>)}</>);
  if (id === 'callout_edge') return wrap(<div style={{ width: '100%', borderRadius: 6, border: '1px solid rgba(124,92,246,0.45)', padding: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
    <div style={{ width: 18, height: 2, borderRadius: 2, background: SPEC }} />{line(38, 3, '#23264B')}{line(46, 2, '#C6CBE4')}</div>);
  if (id === 'signature_minimal') return wrap(<div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
    <div style={{ width: 16, height: 16, borderRadius: '50%', background: SPEC }} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{line(28, 2.5, '#23264B')}{line(20, 2, '#C6CBE4')}</div></div>);
  if (id === 'feature_row_3') return wrap(<div style={{ display: 'flex', gap: 5 }}>
    {[0, 1, 2].map((n) => <div key={n} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ width: 10, height: 2, background: '#00E5C3', borderRadius: 1 }} />{line(16, 2.5, '#23264B')}{line(16, 2, '#C6CBE4')}</div>)}</div>);
  if (id === 'stat_counter') return wrap(<>
    <div style={{ width: 18, height: 2.5, borderRadius: 2, background: SPEC }} />
    <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 14, color: '#00B894' }}>$1,400</span></>);
  if (['stat_callout', 'big_number_hero', 'pricing_line'].includes(id)) return wrap(<>
    <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 15, background: SPEC, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>6+ hrs</span>{line(40)}</>);
  if (id === 'stat_trio' || id === 'benchmark_bar') return wrap(<div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
    <div style={{ width: 10, height: 14, borderRadius: 2, background: '#FF2E9A' }} /><div style={{ width: 10, height: 22, borderRadius: 2, background: '#00E5C3' }} /><div style={{ width: 10, height: 18, borderRadius: 2, background: '#38B6FF' }} /></div>);
  if (id.startsWith('before_after') || id === 'your_day_vs' || id === 'problem_solution_split') return wrap(<div style={{ display: 'flex', gap: 4 }}>
    <div style={{ width: 26, height: 34, borderRadius: 4, background: 'rgba(255,46,154,0.18)', border: '1px solid rgba(255,46,154,0.5)' }} />
    <div style={{ width: 26, height: 34, borderRadius: 4, background: 'rgba(0,229,195,0.18)', border: '1px solid rgba(0,229,195,0.5)' }} /></div>);
  if (['cta_button', 'book_a_call', 'leak_check_offer_card', 'urgency_nudge', 'walk_away_checklist'].includes(id)) return wrap(<>
    {line(46)}{line(34)}<div style={{ width: 44, height: 12, borderRadius: 999, background: SPEC, marginTop: 2 }} /></>);
  if (['greeting_line', 'signature_only'].includes(id)) return wrap(<div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
    <div style={{ width: 14, height: 14, borderRadius: '50%', background: SPEC }} /><div style={{ display: 'grid', gap: 3 }}>{line(34)}{line(24)}</div></div>);
  if (['testimonial_quote', 'pull_quote', 'mini_case_study'].includes(id)) return wrap(<>
    <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 16, color: '#7C5CF6', lineHeight: 0.6 }}>“</span>{line(46)}{line(38)}</>);
  if (id === 'highlight_line') return wrap(<>
    <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 13, background: SPEC, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Gradient</span>{line(38)}</>);
  if (['comparison_table', 'with_without_table', 'manual_vs_automated', 'two_option_offer'].includes(id)) return wrap(<div style={{ display: 'grid', gap: 3 }}>
    {[0, 1].map((n) => <div key={n} style={{ display: 'flex', gap: 4 }}>{line(20)}{line(16, 3, '#00E5C3')}{line(16, 3, '#FF2E9A')}</div>)}</div>);
  if (['money_recovered_bar', 'time_saved_clock', 'roi_snapshot', 'industry_benchmark_dial', 'cost_of_waiting'].includes(id)) return wrap(<>
    <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 14, color: id === 'cost_of_waiting' ? '#FF2E9A' : '#00E5C3' }}>{id === 'time_saved_clock' ? '6 hrs' : '$1.4k'}</span>{line(40)}</>);
  if (['horizontal_timeline', 'workflow_map', 'integration_strip', 'step_cards'].includes(id)) return wrap(<div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
    {[0, 1, 2].map((n) => <React.Fragment key={n}><div style={{ width: 12, height: 12, borderRadius: 3, background: n === 1 ? '#38B6FF' : '#2A2070' }} />{n < 2 && <span style={{ color: '#00E5C3', fontSize: 9 }}>→</span>}</React.Fragment>)}</div>);
  if (['logo_strip', 'star_rating_row', 'google_review_card', 'guarantee_badge', 'named_result_card'].includes(id)) return wrap(<>
    <span style={{ color: '#F0C36D', fontSize: 11, letterSpacing: 1 }}>★★★★★</span>{line(40)}{line(30)}</>);
  if (['leak_check_hero', 'blueprint_teaser', 'book_a_time_embed'].includes(id)) return wrap(<div style={{ width: 60, height: 40, borderRadius: 6, background: '#100838', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
    <div style={{ width: 36, height: 4, borderRadius: 2, background: SPEC }} />{line(28, 3, '#5A628C')}</div>);
  if (['pattern_interrupt', 'question_open', 'story_open', 'seasonal_hook'].includes(id)) return wrap(<>
    <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 14, color: '#38B6FF' }}>?!</span>{line(44)}{line(34)}</>);
  if (['phone_cta', 'sms_cta'].includes(id)) return wrap(<>
    <span style={{ fontSize: 13 }}>{id === 'phone_cta' ? '☎' : '✉'}</span><div style={{ width: 40, height: 10, borderRadius: 999, background: 'rgba(0,229,195,0.35)' }} /></>);
  if (id === 'ps_line') return wrap(<><span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 12, color: '#7C5CF6' }}>P.S.</span>{line(42)}</>);
  if (['merge_snippet', 'industry_line', 'dynamic_pain_line', 'location_line'].includes(id)) return wrap(<>
    <div style={{ padding: '1px 7px', borderRadius: 999, background: 'rgba(124,92,246,0.25)', border: '1px solid rgba(124,92,246,0.5)', fontFamily: 'Inter', fontSize: 8, color: '#B9A8FF', fontWeight: 700 }}>{'{{name}}'}</div>{line(40)}</>);
  if (id === 'divider') return wrap(<div style={{ width: 50, height: 3, borderRadius: 2, background: SPEC }} />);
  if (id === 'spacer') return wrap(<div style={{ width: 40, height: 22, borderRadius: 4, border: '1px dashed #C6CBE4' }} />);
  if (id === 'callout_box') return wrap(<div style={{ width: 56, height: 34, borderRadius: 5, border: '1.5px solid #00E5C3', display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', justifyContent: 'center' }}>{line(34)}{line(26)}</div>);
  if (['bullet_points', 'numbered_steps', 'three_step_simple', 'flow_strip', 'walk_away_checklist'].includes(id)) return wrap(<div style={{ display: 'grid', gap: 4 }}>
    {[0, 1, 2].map((n) => <div key={n} style={{ display: 'flex', gap: 4, alignItems: 'center' }}><div style={{ width: 5, height: 5, borderRadius: '50%', background: '#00E5C3' }} />{line(36)}</div>)}</div>);
  if (group === 'visual') return wrap(<div style={{ width: 52, height: 30, borderRadius: 5, background: 'linear-gradient(135deg,#38B6FF33,#C448F033)', border: '1px solid #C6CBE4' }} />);
  return wrap(<>{line(50)}{line(44)}{line(30)}</>);
};

const CATS = [
  { id: 'open', name: 'Open' }, { id: 'proof', name: 'Proof' }, { id: 'process', name: 'Process' },
  { id: 'contrast', name: 'Contrast' }, { id: 'trust', name: 'Trust' }, { id: 'offer', name: 'Offer' },
  { id: 'act', name: 'Act' }, { id: 'personalisation', name: 'Personal' }, { id: 'plain', name: 'Structure' },
  { id: 'custom', name: 'Custom' },
];
const ACCENTS = [
  { id: 'spectrum', label: 'Spectrum', swatch: 'linear-gradient(95deg,#00E5C3,#38B6FF,#7C5CF6,#C448F0,#FF2E9A)' },
  { id: 'violet', label: 'Violet', swatch: '#7C5CF6' },
  { id: 'magenta', label: 'Magenta', swatch: '#FF2E9A' },
];
const TEST_INBOX = 'michael@procloud.com.au';
const NO_AI_FILL = new Set(['divider', 'spacer']);
const CHAT_CHIPS = ['Shorten', 'Warmer', 'Punchier', 'Add proof'];
const TONE_PROMPTS = { Shorten: 'Shorten it — keep the meaning, cut the padding.', Warmer: 'Make the tone warmer and more human.', Punchier: 'Make it punchier — sharper, more direct.', 'Add proof': 'Add the best-matching proof point for this recipient.' };
const STARTERS = [
  { label: 'Cold intro', prompt: 'Write a warm first-contact email introducing the free Leak Check — one sharp hook about time/money leaking, one proof point, low-pressure ask.' },
  { label: 'Follow-up', prompt: 'Write a short friendly follow-up to an earlier email that got no reply — one new angle, easy out, gentle Leak Check reminder.' },
  { label: 'Re-engage', prompt: 'Write a re-engagement email for a lead who went quiet weeks ago — acknowledge the gap, one fresh insight, soft invitation.' },
];
const DEPTH_META = {
  punchy: { sym: '⚡', label: 'Punchy', hint: '4-5 sections · quick note' },
  balanced: { sym: '◆', label: 'Balanced', hint: 'the killer layout' },
  deep: { sym: '▣', label: 'Deep', hint: 'room to explain · anchored' },
};
const LINT_SECTIONS = [['delivery', 'Delivery'], ['design', 'Design curator'], ['pr', 'PR manager']];
const PROSE_BLOCKS = new Set(['paragraph', 'story_open', 'question_open', 'pattern_interrupt', 'dynamic_pain_line', 'seasonal_hook']);
const ANCHOR_ICON_MAP = [
  [['missed call', 'voicemail', 'unanswered'], 'phone-missed'], [['call', 'phone'], 'phone-call'],
  [['invoice', 'paid', 'payment', 'cash'], 'banknote'], [['quote'], 'file-check'],
  [['leak', 'losing', 'loses'], 'droplet'], [['agent', 'automat', 'ai ', 'system', 'bot'], 'bot'],
  [['hour', 'time', 'week', 'admin'], 'clock'], [['grow', 'revenue', 'result'], 'trending-up'],
  [['guarantee', 'risk', 'trust'], 'shield-check'], [['book', 'calendar', '30 min'], 'calendar-check'],
  [['customer', 'client', 'follow'], 'users'], [['money', 'dollar', 'cost'], 'dollar-sign'],
];
const pickAnchorIcon = (text) => {
  const t = (text || '').toLowerCase();
  for (const [keys, icon] of ANCHOR_ICON_MAP) if (keys.some((k) => t.includes(k))) return icon;
  return 'lightbulb';
};

export default function V2EmailComposer({ onClose, campaigns, showToast, leadContext = null, initialDraft = null }) {
  const [library, setLibrary] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [lockedTpl, setLockedTpl] = useState(false);
  const [subject, setSubject] = useState('');
  const [frame, setFrame] = useState({});
  const [blocks, setBlocks] = useState([]);
  const [hasDraft, setHasDraft] = useState(false);
  const [frags, setFrags] = useState(null);
  const [selIdx, setSelIdx] = useState(null);
  const [regenIdx, setRegenIdx] = useState(null);
  const [fillingIdx, setFillingIdx] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null); // null = insert · {idx, field, itemIdx} = swap
  const [cat, setCat] = useState('all');
  const [myBlocks, setMyBlocks] = useState([]);
  const [brandAccent, setBrandAccent] = useState('spectrum');
  const [gradText, setGradText] = useState('Book your free ==Leak Check== — 30 minutes, no obligation');
  const [testBusy, setTestBusy] = useState(false);
  const [sendOk, setSendOk] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [gradFlash, setGradFlash] = useState(false);
  const [orbSettle, setOrbSettle] = useState(false);
  const [motionOn, setMotionOn] = useState(motionEnabled());
  const [mode, setMode] = useState('compose');
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [heroInput, setHeroInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [genStage, setGenStage] = useState(null);
  const [suggests, setSuggests] = useState([]);
  const [depth, setDepth] = useState(null);
  const [depthPick, setDepthPick] = useState(false);
  const [leadNotes, setLeadNotes] = useState(leadContext?.notes || '');
  const [oneOff, setOneOff] = useState(false);
  const [briefMention, setBriefMention] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [leadQ, setLeadQ] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [snaps, setSnaps] = useState([]);
  const [pacing, setPacing] = useState(null);
  const [leadSends, setLeadSends] = useState([]);
  const [allSends, setAllSends] = useState([]);
  const [blockedAttempts, setBlockedAttempts] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: leadContext?.email || '', first_name: leadContext?.first_name || '', business_name: leadContext?.business_name || '' });
  const [busy, setBusy] = useState(false);
  const [attachCampaign, setAttachCampaign] = useState('');
  const [wide, setWide] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1160 : true);
  const [mobilePanel, setMobilePanel] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);
  const [headerOpen, setHeaderOpen] = useState(false);
  const [footerOpen, setFooterOpen] = useState(false);
  const [linkedLead, setLinkedLead] = useState(leadContext);
  const [touchedFrame, setTouchedFrame] = useState([]);
  const [touchedSubject, setTouchedSubject] = useState(false);
  const [subjectSuggs, setSubjectSuggs] = useState(null);
  const [subjBusy, setSubjBusy] = useState(false);
  const closeEditors = () => { setSelIdx(null); setHeaderOpen(false); setFooterOpen(false); };
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeEditors(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [lint, setLint] = useState(null);
  const [lintBusy, setLintBusy] = useState(false);
  const [fbSim, setFbSim] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  useEffect(() => {
    if (mode !== 'preview') return;
    setEmailPreviewHtml('');
    post('/admin/outreach/render_blocks', { frame, blocks: blocks.map(({ _ai, _uid, user_touched, ...b }) => b), ...(Object.keys(recipient()).length ? { contact: recipient() } : {}) })
      .then((d) => setEmailPreviewHtml(d.html || ''))
      .catch(() => setEmailPreviewHtml('<p style="color:#fff;font-family:sans-serif;padding:40px">Preview failed to render.</p>'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);
  const dragRef = useRef(null);
  const debounceRef = useRef(null);
  const chatEndRef = useRef(null);
  const gradRef = useRef(null);
  const gradSelRef = useRef({ s: 0, e: 0 });
  const blocksRef = useRef([]);
  const voiceTimer = useRef({});

  const uidGen = () => Math.random().toString(36).slice(2, 10);
  const blockText = (b) => Object.values(b?.fields || {}).map((v) => (Array.isArray(v) ? v.join('\n') : v)).filter((v) => typeof v === 'string').join('\n');
  const stripMeta = (bl) => bl.map(({ _ai, _uid, user_touched, ...b }) => b);

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1160);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    ensureMotionStyles();
    get('/admin/outreach/blocks').then(setLibrary).catch((e) => showToast('Library load failed: ' + e.message));
    get('/admin/outreach/templates').then((d) => setTemplates(d.templates || [])).catch(() => {});
    get('/admin/outreach/pacing').then(setPacing).catch(() => {});
    get('/admin/outreach/proofs').then((d) => setProofs(d.proofs || [])).catch(() => {});
    get('/admin/outreach/custom_blocks').then((d) => setMyBlocks(d.custom_blocks || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  useEffect(() => { if (mode !== 'preview') setFbSim(false); }, [mode]);
  const prevBusyRef = useRef(false);
  useEffect(() => {
    const was = prevBusyRef.current;
    prevBusyRef.current = chatBusy;
    if (!chatBusy && was) { setOrbSettle(true); const t = setTimeout(() => setOrbSettle(false), 450); return () => clearTimeout(t); }
  }, [chatBusy]);
  useEffect(() => {
    if (linkedLead?.lead_id) get(`/admin/os/leads/${linkedLead.lead_id}/sends`).then((d) => setLeadSends(d.sends || [])).catch(() => {});
  }, [linkedLead]);
  useEffect(() => {
    if (mode === 'history') get('/admin/outreach/sends_recent').then((d) => { setAllSends(d.sends || []); setBlockedAttempts(d.blocked_attempts || []); }).catch(() => {});
  }, [mode]);
  const createLeadFromSend = async (sendId) => {
    try {
      const d = await post(`/admin/outreach/sends/${sendId}/create_lead`, {});
      showToast(d.created ? `✓ Lead created: ${d.lead.first_name} — send linked` : `✓ Linked to existing lead: ${d.lead.first_name}`);
      const r = await get('/admin/outreach/sends_recent');
      setAllSends(r.sends || []); setBlockedAttempts(r.blocked_attempts || []);
    } catch (e) { showToast('Create lead failed: ' + e.message); }
  };
  useEffect(() => {
    if (initialDraft) {
      setSubject(initialDraft.subject || ''); setFrame(initialDraft.frame || {});
      setBlocks((initialDraft.blocks || []).map((b) => ({ ...b, _uid: uidGen(), _ai: blockText(b) })));
      setHasDraft(true);
      setChat([{ role: 'assistant', content: `Loaded your queued follow-up draft${initialDraft.suggested_date ? ` (suggested ${initialDraft.suggested_date})` : ''}. Tweak anything — it only ever sends through the normal gate.` }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat, chatBusy]);
  useEffect(() => {
    const pb = document.body.style.overflow, ph = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden'; document.documentElement.style.overflow = 'hidden';
    return () => { document.body.style.overflow = pb; document.documentElement.style.overflow = ph; };
  }, []);

  const recipient = useCallback(() => {
    const r = {};
    if (sendForm.first_name) r.first_name = sendForm.first_name;
    if (sendForm.business_name) r.business_name = sendForm.business_name;
    if (sendForm.to_email) r.email = sendForm.to_email;
    return r;
  }, [sendForm]);

  const refreshFrags = useCallback((fr, bl) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const d = await post('/admin/outreach/render_studio', { frame: fr, blocks: bl.map(({ _ai, _uid, user_touched, ...b }) => b), ...(Object.keys(recipient()).length ? { contact: recipient() } : {}), ...(fbSim ? { simulate_fallback: true } : {}) });
        setFrags(d);
      } catch (e) { /* best-effort */ }
    }, 400);
  }, [recipient, fbSim]);
  useEffect(() => { if (hasDraft) refreshFrags(frame, blocks); }, [frame, blocks, hasDraft, refreshFrags]);

  const lastSend = leadSends[0] || null;
  const aiContact = () => ({
    first_name: sendForm.first_name || '', business_name: sendForm.business_name || '',
    email: sendForm.to_email || '', notes: (leadNotes || '').slice(0, 800),
    ...(linkedLead ? { stage: linkedLead.stage || '', source: linkedLead.source || '' } : {}),
    ...(lastSend ? { last_sent_at: (lastSend.sent_at || '').slice(0, 10), last_opened: !!lastSend.opened_at, last_replied: !!lastSend.replied_at } : {}),
  });
  const emailContext = () => {
    const texts = blocks.slice(0, 5).map((b) => JSON.stringify(b.fields || {}).slice(0, 60)).join(' ');
    return `Subject: ${subject}. Recipient: ${sendForm.first_name} at ${sendForm.business_name}. Blocks: ${blocks.map((b) => b.block_id).join(', ')}. ${texts}`.slice(0, 480);
  };
  const fallbackSuggests = (bl) => {
    const have = new Set(bl.map((b) => b.block_id));
    return ['stat_callout', 'leak_check_offer_card', 'testimonial_quote', 'book_a_call', 'bullet_points'].filter((id) => !have.has(id)).slice(0, 3);
  };
  const bestProof = () => {
    if (!proofs.length) return null;
    const hay = `${sendForm.business_name} ${subject} ${JSON.stringify(blocks).slice(0, 400)}`.toLowerCase();
    let best = proofs[0], bestScore = -1;
    proofs.forEach((p) => {
      const inds = p.industries || [];
      const lks = p.leaks || [];
      const iScore = inds.filter((t) => String(t).toLowerCase().split(/[\s&/]+/).some((w) => w.length > 3 && hay.includes(w))).length;
      const lScore = lks.filter((t) => String(t).toLowerCase().split(/[_\s]+/).some((w) => w.length > 3 && hay.includes(w))).length;
      const score = iScore * 10 + lScore + (p.kind === 'case' ? 0.5 : 0); // industry first, then leaks; real results break ties
      if (score > bestScore) { bestScore = score; best = p; }
    });
    return best;
  };

  const pushSnap = () => setSnaps((s) => [...s.slice(-9), { subject, frame: { ...frame }, blocks: JSON.parse(JSON.stringify(blocks)) }]);
  const undo = (snapIdx) => {
    const snap = snaps[snapIdx];
    if (!snap) return;
    setSubject(snap.subject); setFrame(snap.frame); setBlocks(snap.blocks);
    showToast('↩ Undone');
  };

  // ── AI chat — context-injected, targeted edits, action chips + undo ────
  const attachLead = (l, note) => {
    setLinkedLead(l);
    setSendForm((f) => ({ to_email: l.email || f.to_email, first_name: l.first_name || f.first_name, business_name: l.business_name || f.business_name }));
    if (l.notes) setLeadNotes(l.notes);
    if (note) showToast(note);
  };
  const saveLeadNotes = async () => {
    if (!linkedLead?.lead_id) return;
    try { await patch(`/admin/os/leads/${linkedLead.lead_id}`, { notes: leadNotes }); showToast('📝 Notes saved to lead'); }
    catch (e) { showToast('Notes save failed: ' + e.message); }
  };
  const createLead = async (sug, msgIdx) => {
    try {
      const d = await post('/admin/os/leads', {
        first_name: sug.first_name || sug.business_name || 'Unknown',
        business_name: sug.business_name || sug.first_name || '—',
        source: 'other', notes: 'Created from Email Studio chat',
      });
      attachLead(d.lead, `✓ Lead created & linked: ${d.lead.first_name}`);
      setChat((c) => c.map((m, x) => (x === msgIdx ? { ...m, leadSuggest: null, action2: `🔗 Lead created · ${d.lead.first_name}${d.lead.business_name ? ' · ' + d.lead.business_name : ''}` } : m)));
    } catch (e) { showToast('Create lead failed: ' + e.message); }
  };
  const sendChat = async (text, opts) => {
    const msg = (text || chatInput).trim();
    if (!msg || chatBusy) return;
    if (lockGuard()) return;
    setChatInput(''); setHeroInput(''); setDepthPick(false);
    setChat((c) => [...c, { role: 'user', content: msg }]);
    setChatBusy(true);
    const snapIdx = snaps.length;
    pushSnap();
    const prev = blocks;
    const fresh = prev.length === 0;
    const payload = {
      instruction: msg, history: chat.slice(-8).map(({ role, content }) => ({ role, content })),
      subject, frame, blocks: blocks.map(({ _ai, _uid, ...b }) => b), contact: opts?.contact || aiContact(),
      user_touched_frame: touchedFrame, user_touched_subject: touchedSubject,
      ...(opts?.depth ? { depth: opts.depth } : {}),
    };
    try {
      let d = null;
      // Streamed path: real pipeline stages + blocks land on the canvas live.
      try {
        const res = await fetch(`${API}/admin/outreach/studio_chat_stream`, { method: 'POST', headers: auth(), body: JSON.stringify(payload) });
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        const liveArr = [];
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop();
          for (const ln of lines) {
            if (!ln.trim()) continue;
            let e; try { e = JSON.parse(ln); } catch { continue; }
            if (e.stage) setGenStage(e.label || e.stage);
            else if (e.type === 'block' && fresh) {
              liveArr[e.index] = { ...e.block, _uid: uidGen() };
              const live = liveArr.filter(Boolean);
              setBlocks(live);
              setHasDraft(true);
              setGenStage(`Writing copy… block ${live.length} on the canvas`);
            } else if (e.type === 'tokens' && !fresh) setGenStage(`Applying your edit… ~${Math.max(1, Math.round(e.chars / 6))} words in`);
            else if (e.type === 'done') d = e.data;
            else if (e.type === 'error') throw new Error(e.detail || 'stream error');
          }
        }
        if (!d) throw new Error('stream ended without result');
      } catch (streamErr) {
        console.warn('[studio] stream path failed — falling back to non-stream:', streamErr);
        setGenStage('Stream unavailable — using standard path…');
        d = await post('/admin/outreach/studio_chat', payload);
      }
      const changed = d.changed_block_indexes || [];
      const nextBlocks = (d.blocks || []).map((b, i) => {
        const p = prev[i];
        // user edits win: keep MJ's exact block when the AI wasn't asked to touch it
        if (p && p.user_touched && p.block_id === b.block_id && !changed.includes(i)) return p;
        if (p && p.block_id === b.block_id && JSON.stringify(p.fields || {}) === JSON.stringify(b.fields || {})) return p;
        return { ...b, _uid: (p && p.block_id === b.block_id && p._uid) || uidGen(), _ai: blockText(b), user_touched: false };
      });
      setSubject(d.subject || subject); setFrame(d.frame || frame); setBlocks(nextBlocks);
      setHasDraft(true);
      setSuggests((d.suggested_block_ids?.length ? d.suggested_block_ids : fallbackSuggests(nextBlocks)));
      const label = changed.length ? `✓ ${changed.length} block${changed.length > 1 ? 's' : ''} ${prev.length ? 'edited' : 'written'}` : '✓ Draft updated';
      const nextDepth = opts?.depth || d.depth || depth || 'balanced';
      setDepth(nextDepth);
      const extra = { depth: nextDepth, archetype: d.archetype || null };
      if (d.matched_lead) {
        attachLead(d.matched_lead);
        extra.action2 = `🔗 Linked to ${d.matched_lead.first_name || ''}${d.matched_lead.business_name ? ' · ' + d.matched_lead.business_name : ''}${d.matched_lead.stage ? ` (${d.matched_lead.stage})` : ''}`;
      } else if (d.create_lead_suggestion) extra.leadSuggest = d.create_lead_suggestion;
      setChat((c) => [...c, { role: 'assistant', content: d.reply || 'Done.', action: label, snapIdx, ...extra }]);
      if (d.voice_check?.triggered) showToast('Voice check auto-fixed the AI copy');
    } catch (e) {
      setChat((c) => [...c, { role: 'assistant', content: `That didn't work: ${e.message}` }]);
    } finally { setChatBusy(false); setGenStage(null); }
  };
  const recomposeAtDepth = (id) => {
    setDepthPick(false);
    sendChat(`Recompose this same email at ${DEPTH_META[id].label} depth — keep the message and content, restructure the layout to the ${id} structure.`, { depth: id });
  };
  // ── Recipient-first flow: smart capture from the brief BEFORE composing ──
  const leadToContact = (l) => ({ first_name: l.first_name || '', business_name: l.business_name || '', email: l.email || '', notes: (l.notes || '').slice(0, 800) });
  const draftFromHero = async () => {
    const brief = heroInput.trim();
    if (!brief || chatBusy || scanBusy) return;
    const hasRec = !!(sendForm.first_name || sendForm.business_name || sendForm.to_email || linkedLead);
    if (hasRec || oneOff) { sendChat(brief); return; }
    setScanBusy(true);
    try {
      const d = await post('/admin/outreach/brief_scan', { brief });
      if (d.matched_lead) {
        attachLead(d.matched_lead, `🔗 Matched lead: ${d.matched_lead.first_name || d.matched_lead.business_name}`);
        sendChat(brief, { contact: leadToContact(d.matched_lead) });
      } else if (d.mention && (d.mention.first_name || d.mention.business_name)) {
        setBriefMention(d.mention);
      } else sendChat(brief);
    } catch { sendChat(brief); } finally { setScanBusy(false); }
  };
  const createFromMention = async () => {
    const m = briefMention; if (!m) return;
    setBriefMention(null);
    try {
      const d = await post('/admin/os/leads', {
        first_name: m.first_name || m.business_name || 'Unknown',
        business_name: m.business_name || '',
        ...(m.email ? { email: m.email } : {}),
        source: 'other', notes: leadNotes || 'Created from Email Studio brief',
      });
      attachLead(d.lead, `✓ Lead created & linked: ${d.lead.first_name}`);
      sendChat(heroInput, { contact: { ...leadToContact(d.lead), notes: (leadNotes || d.lead.notes || '').slice(0, 800) } });
    } catch (e) { showToast('Create lead failed: ' + e.message); sendChat(heroInput); }
  };
  useEffect(() => {
    if (leadQ.trim().length < 2) { setLeadResults([]); return; }
    const t = setTimeout(() => {
      get(`/admin/os/leads?q=${encodeURIComponent(leadQ.trim())}`)
        .then((d) => setLeadResults((d.leads || d.rows || []).slice(0, 6)))
        .catch(() => setLeadResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [leadQ]);
  const toneChip = (label) => {
    const p = TONE_PROMPTS[label];
    if (selIdx !== null && blocks[selIdx]) { pushSnap(); regenBlock(selIdx, p, true); }
    else sendChat(p);
  };

  // ── block ops ──────────────────────────────────────────────────────────
  const blockDef = (id) => library?.blocks.find((b) => b.id === id);
  const move = (i, dir) => {
    if (lockGuard()) return;
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next); setSelIdx(null);
  };
  const removeBlock = (i) => { if (lockGuard()) return; setBlocks(blocks.filter((_, x) => x !== i)); setSelIdx(null); };
  const duplicateBlock = (i) => { if (lockGuard()) return; const n = [...blocks]; n.splice(i + 1, 0, { ...JSON.parse(JSON.stringify(blocks[i])), _uid: uidGen() }); setBlocks(n); };
  const setBlockField = (i, k, v) => {
    if (lockGuard()) return;
    setBlocks((cur) => cur.map((b, x) => (x === i ? { ...b, user_touched: true, fields: { ...(b.fields || {}), [k]: v } } : b)));
    const b = blocks[i];
    if (b?._ai) {
      const key = b._uid || `i${i}`;
      clearTimeout(voiceTimer.current[key]);
      voiceTimer.current[key] = setTimeout(() => {
        const cur = (b._uid && blocksRef.current.find((x) => x._uid === b._uid)) || blocksRef.current[i];
        if (cur?._ai) {
          const after = blockText(cur);
          if (after && after !== cur._ai) post('/admin/outreach/voice_sample', { block_id: cur.block_id, before: cur._ai, after }).catch(() => {});
        }
      }, 3000);
    }
  };

  const applyBrand = (blockId, fields) => {
    const out = { ...fields };
    if (blockDef(blockId)?.fields?.some((f) => f.key === 'accent')) out.accent = brandAccent;
    if (blockId === 'cta_button' && brandAccent === 'spectrum') out.style = 'gradient';
    return out;
  };

  const addBlock = async (blockId, at = null, presetFields = null) => {
    if (lockGuard()) return;
    const idx = at === null ? blocks.length : at;
    pushSnap();
    const skipAI = presetFields !== null || NO_AI_FILL.has(blockId);
    const initFields = presetFields ? JSON.parse(JSON.stringify(presetFields)) : {};
    const next = [...blocks];
    next.splice(idx, 0, { block_id: blockId, fields: initFields, _uid: uidGen(), ...(presetFields ? { user_touched: true } : {}) });
    setBlocks(next); setHasDraft(true); setMobilePanel(null);
    setSuggests((s) => s.filter((id) => id !== blockId));
    if (skipAI) { showToast(`✓ ${blockDef(blockId)?.name || blockId} inserted`); return; }
    setFillingIdx(idx);
    try {
      const d = await post('/admin/outreach/regen_block', {
        block_id: blockId, fields: {}, context: emailContext(),
        note: 'Fill this new block with content relevant to this email topic and this recipient. Use the best-matching proof for stats.',
      });
      const filled = applyBrand(blockId, d.fields || {});
      setBlocks((cur) => cur.map((b, k) => (k === idx && b.block_id === blockId ? { ...b, fields: filled, _ai: blockText({ fields: filled }), user_touched: false } : b)));
      setChat((c) => [...c, { role: 'assistant', content: `Added a ${blockDef(blockId)?.name || blockId} filled for this email.`, action: `✓ ${blockDef(blockId)?.name || blockId} inserted`, snapIdx: snaps.length }]);
    } catch (e) { showToast('AI fill failed — block added with sample copy'); }
    finally { setFillingIdx(null); }
  };

  const openIconPicker = (target = null) => { setPickerTarget(target); setPickerOpen(true); };
  const handleIconPick = (pick) => {
    setPickerOpen(false);
    const t = pickerTarget;
    setPickerTarget(null);
    if (t) { // swap mode
      pushSnap();
      if (t.field === 'items') {
        const items = [...(blocks[t.idx]?.fields?.items || [])];
        const parts = String(items[t.itemIdx] || '||').split('|');
        items[t.itemIdx] = [pick.kind === 'icon' ? pick.name : 'sparkles', parts[1] || '', parts[2] || ''].join('|');
        setBlockField(t.idx, 'items', items);
      } else {
        setBlockField(t.idx, t.field, pick.name);
        if (pick.kind === 'icon' && t.field === 'icon') setBlockField(t.idx, 'icon_variant', pick.variant);
        if (pick.kind === 'icon' && blocks[t.idx]?.block_id === 'icon_badge') setBlockField(t.idx, 'variant', pick.variant);
      }
      showToast('✓ Icon swapped');
      return;
    }
    // A block is selected → the icon fills THAT block's slot. No orphan rows.
    if (pick.kind === 'icon' && selIdx !== null && blocks[selIdx] && !['icon_badge', 'icon_feature_row', 'icon_strip', 'spot_graphic'].includes(blocks[selIdx].block_id)) {
      pushSnap();
      const bid = blocks[selIdx].block_id;
      setBlockField(selIdx, 'icon', pick.name);
      if (bid !== 'leak_check_hero') setBlockField(selIdx, 'icon_variant', pick.variant);
      showToast(`✓ ${pick.name} set on the ${blockDef(bid)?.name || 'selected'} block`);
      return;
    }
    const at = selIdx !== null ? selIdx + 1 : null; // insert after selection, else at end
    if (pick.kind === 'icon') {
      // No block selected → icons never stand alone; seed a labelled icon strip.
      const label = pick.name.split('-').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
      pushSnap();
      addBlock('icon_strip', at, { items: [`${pick.name}|${label}`] });
      showToast('✓ Icon strip added — select it to add up to 3 more icons');
    }
    else if (pick.kind === 'spot') addBlock('spot_graphic', at);
    else addBlock('stat_gauge', at);
  };

  const regenBlock = async (i, note = '', snapped = false) => {    if (regenIdx !== null) return;
    if (lockGuard()) return;
    const snapIdx = snaps.length;
    if (!snapped) pushSnap();
    setRegenIdx(i);
    try {
      const b = blocks[i];
      const d = await post('/admin/outreach/regen_block', { block_id: b.block_id, fields: b.fields || {}, context: emailContext(), note });
      const keep = {};
      if (b.fields?.icon) { keep.icon = b.fields.icon; keep.icon_variant = b.fields.icon_variant; }
      setBlocks((cur) => cur.map((x, k) => (k === i ? { ...x, fields: { ...(d.fields || {}), ...keep }, _ai: blockText({ fields: d.fields }), user_touched: false } : x)));
      showToast('✓ Block rewritten on brand');
      setChat((c) => [...c, { role: 'assistant', content: `Rewrote the ${blockDef(b.block_id)?.name || b.block_id} block on-brand.`, action: '✦ Block rewritten', snapIdx }]);
    } catch (e) { showToast('Rewrite failed: ' + e.message); }
    finally { setRegenIdx(null); }
  };

  // ── drag & drop ────────────────────────────────────────────────────────
  const onBlockDragStart = (e, i) => { e.dataTransfer.setData('text/plain', String(i)); dragRef.current = { kind: 'reorder', from: i }; setSelIdx(null); };
  const onBlockDragOver = (e, i) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    setDropIdx(e.clientY < r.top + r.height / 2 ? i : i + 1);
  };
  const onCanvasDragOver = (e) => { if (dragRef.current) { e.preventDefault(); if (dropIdx === null) setDropIdx(blocks.length); } };
  const onCanvasDrop = (e) => {
    e.preventDefault();
    if (lockGuard()) { dragRef.current = null; setDropIdx(null); return; }
    const drag = dragRef.current, at = dropIdx === null ? blocks.length : dropIdx;
    dragRef.current = null; setDropIdx(null);
    if (!drag) return;
    if (drag.kind === 'add') { addBlock(drag.blockId, at); return; }
    let to = at; const from = drag.from;
    if (from < to) to -= 1;
    if (to === from || to < 0) return;
    const next = [...blocks]; const [it] = next.splice(from, 1); next.splice(to, 0, it);
    setBlocks(next);
  };
  const onDragEnd = () => { dragRef.current = null; setDropIdx(null); };

  // ── templates / send / attach ──────────────────────────────────────────
  const lockGuard = () => {
    if (lockedTpl) { showToast('🔒 Locked template — recipient + send only'); return true; }
    return false;
  };
  const saveTemplate = async () => {
    if (lockGuard()) return;
    const name = prompt('Template name:');
    if (!name) return;
    try {
      await post('/admin/outreach/templates', { name, subject, frame, blocks: stripMeta(blocks) });
      const d = await get('/admin/outreach/templates'); setTemplates(d.templates || []);
      showToast(`✓ Saved “${name}”`);
    } catch (e) { showToast('Save failed: ' + e.message); }
  };
  const loadTemplate = (t) => { setSubject(t.subject || ''); setFrame(t.frame || {}); setBlocks(t.blocks || []); setHasDraft(true); setLockedTpl(!!t.locked); setSelIdx(null); setSuggests(fallbackSuggests(t.blocks || [])); showToast(t.locked ? `🔒 Loaded “${t.name}” — locked, recipient + send only` : `Loaded “${t.name}”`); };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template “${t.name}”?`)) return;
    try {
      const r = await fetch(`${API}/admin/outreach/templates/${t.template_id}`, { method: 'DELETE', headers: auth() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setTemplates(templates.filter((x) => x.template_id !== t.template_id));
    } catch (e) { showToast('Delete failed: ' + e.message); }
  };
  const logAttempt = (reason) => {
    post('/admin/outreach/send_attempt_log', { outcome: 'blocked_client', reason, to_email: sendForm.to_email, subject }).catch(() => {});
  };
  const openSendModal = () => {
    if (!blocks.length) { showToast('⛔ Add at least one block before sending'); logAttempt('no_blocks'); return; }
    if (!subject.trim()) { showToast('⛔ Add a subject line first'); logAttempt('no_subject'); return; }
    if (chatBusy) { showToast('⏳ AI is still writing — one moment'); return; }
    setSendOpen(true); runLint();
  };
  const trySend = () => {
    if (busy) return;
    if (!sendForm.to_email.includes('@')) { showToast('⛔ Recipient email needed — fill the To field'); logAttempt('no_recipient'); return; }
    if (lintBusy) { showToast('⏳ Pre-send check still running…'); return; }
    if (lint?.blocking) { showToast('⛔ Blocked — fix the ✕ items in the pre-send check'); logAttempt('lint_blocking'); return; }
    doSend();
  };

  const doSend = async (overrideCap = false) => {
    setBusy(true);
    try {
      const d = await post('/admin/outreach/send_composed', { ...sendForm, subject, frame, blocks: stripMeta(blocks), confirm: true, ...(overrideCap ? { override_cap: true } : {}), ...(linkedLead?.lead_id ? { lead_id: linkedLead.lead_id } : {}) });
      showToast(`✓ Sent to ${d.sent_to}${d.test_mode ? ' (test mode redirect)' : ''}`);
      setSendOk(true);
      setTimeout(() => { setSendOpen(false); setSendOk(false); }, 950);
      get('/admin/outreach/pacing').then(setPacing).catch(() => {});
    } catch (e) {
      if (e.detail?.code === 'daily_cap_reached') {
        const go = window.confirm(`Warm-up cap reached: ${e.detail.sent_today}/${e.detail.cap} today (week ${e.detail.week}). Exceeding it can hurt deliverability. Send anyway?`);
        if (go) { await doSend(true); return; }
        showToast('Held — daily warm-up cap respected');
      } else showToast('Send failed: ' + e.message);
    } finally { setBusy(false); }
  };
  // ── Custom tab — save-your-own blocks + test send ──────────────────────
  const loadMyBlocks = () => get('/admin/outreach/custom_blocks').then((d) => setMyBlocks(d.custom_blocks || [])).catch(() => {});
  const saveMyBlock = async (i, srcEl = null) => {
    const b = blocks[i];
    if (!b) return;
    const name = prompt('Name this block for My Blocks:', blockDef(b.block_id)?.name || b.block_id);
    if (!name) return;
    try {
      await post('/admin/outreach/custom_blocks', { name, block_id: b.block_id, fields: b.fields || {} });
      loadMyBlocks();
      runOnce(srcEl, 'km-star-run', 550);
      showToast(`✓ Saved to My Blocks — “${name}”`);
    } catch (e) { showToast('Save block failed: ' + e.message); }
  };
  const deleteMyBlock = async (mb) => {
    if (!window.confirm(`Delete “${mb.name}” from My Blocks?`)) return;
    try {
      const r = await fetch(`${API}/admin/outreach/custom_blocks/${mb.custom_id}`, { method: 'DELETE', headers: auth() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setMyBlocks(myBlocks.filter((x) => x.custom_id !== mb.custom_id));
    } catch (e) { showToast('Delete failed: ' + e.message); }
  };
  const sendTest = async (overrideCap = false) => {
    if (testBusy) return;
    if (!blocks.length || !subject.trim()) { showToast('⛔ Need a subject and at least one block first'); logAttempt('test_send_incomplete'); return; }
    setTestBusy(true);
    try {
      const d = await post('/admin/outreach/send_composed', {
        to_email: TEST_INBOX, first_name: sendForm.first_name || 'Michael', business_name: sendForm.business_name || '',
        subject, frame, blocks: stripMeta(blocks), confirm: true, is_test: true,
        ...(overrideCap ? { override_cap: true } : {}),
      });
      showToast(`✓ Test sent to ${d.sent_to} — check the inbox`);
      setTestSent(true);
      setTimeout(() => setTestSent(false), 2400);
      get('/admin/outreach/pacing').then(setPacing).catch(() => {});
    } catch (e) {
      if (e.detail?.code === 'daily_cap_reached') {
        const go = window.confirm(`Warm-up cap reached: ${e.detail.sent_today}/${e.detail.cap} today. Send the test anyway?`);
        if (go) { setTestBusy(false); await sendTest(true); return; }
        showToast('Held — daily warm-up cap respected');
      } else showToast('Test send failed: ' + e.message);
    } finally { setTestBusy(false); }
  };

  const attachToCampaign = async () => {
    if (!attachCampaign) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/outreach/campaigns/${attachCampaign}`, { method: 'PATCH', headers: auth(), body: JSON.stringify({ composer: { frame, blocks: stripMeta(blocks) }, subject }) });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      showToast('✓ Attached');
    } catch (e) { showToast('Attach failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const markFrame = (k) => setTouchedFrame((t) => (t.includes(k) ? t : [...t, k]));
  const frameVal = (k) => (frame[k] !== undefined ? frame[k] : (library?.frame_defaults?.[k] || ''));
  const frameField = (k, area = false) => (
    <label key={k} style={{ fontSize: 11, color: SUB, fontFamily: 'Inter', display: 'block', marginTop: 6 }}>{k}
      {area ? <textarea data-testid={`composer-frame-${k}`} rows={3} value={frameVal(k)} onChange={(e) => { setFrame({ ...frame, [k]: e.target.value }); markFrame(k); }} style={{ ...inputS, marginTop: 3 }} />
        : <input data-testid={`composer-frame-${k}`} value={frameVal(k)} onChange={(e) => { setFrame({ ...frame, [k]: e.target.value }); markFrame(k); }} style={{ ...inputS, marginTop: 3 }} />}
    </label>
  );

  const suggestSubjects = async () => {
    if (subjBusy) return;
    setSubjBusy(true);
    try {
      const d = await post('/admin/outreach/subject_suggest', { subject, frame, blocks: stripMeta(blocks), contact: aiContact() });
      setSubjectSuggs(d.suggestions || []);
    } catch (e) { showToast('Subject ideas failed: ' + e.message); }
    finally { setSubjBusy(false); }
  };

  const runLint = async () => {
    setLintBusy(true); setLint(null);
    try {
      const d = await post('/admin/outreach/presend_lint', { subject, frame, blocks: stripMeta(blocks), contact: recipient() });
      setLint(d);
    } catch (e) { setLint({ blocking: false, checks: [{ id: 'error', label: 'Checker unavailable', status: 'warn', detail: e.message }] }); }
    finally { setLintBusy(false); }
  };
  // Recipient fields changed while the send modal is open → re-lint (debounced)
  // so a stale blocking result can never wedge the Send button.
  const relintTimer = useRef(null);
  useEffect(() => {
    if (!sendOpen) return;
    clearTimeout(relintTimer.current);
    relintTimer.current = setTimeout(runLint, 600);
    return () => clearTimeout(relintTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendForm.to_email, sendForm.first_name, sendForm.business_name]);

  // ── LEFT island — block gallery ────────────────────────────────────────
  const galleryItems = () => {
    if (!library) return [];
    if (cat !== 'all') return library.blocks.filter((b) => b.group === cat).map((b) => ({ type: 'block', b }));
    const items = [];
    const sug = library.blocks.filter((b) => suggests.includes(b.id));
    if (sug.length) {
      items.push({ type: 'header', label: '✦ Suggested for this email' });
      sug.forEach((b) => items.push({ type: 'block', b }));
    }
    (library.groups || []).forEach((g) => {
      const bl = library.blocks.filter((b) => b.group === g.id && !suggests.includes(b.id));
      if (!bl.length) return;
      items.push({ type: 'header', label: `${g.name} · ${bl.length}` });
      bl.forEach((b) => items.push({ type: 'block', b }));
    });
    return items;
  };
  const galleryCard = (b) => {
    const suggested = suggests.includes(b.id);
    return (
      <div key={b.id} data-testid={`palette-chip-${b.id}`}
        className={`km-chip${suggested ? ' km-suggest' : ''}`}
        draggable
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', b.id); dragRef.current = { kind: 'add', blockId: b.id }; }}
        onDragEnd={onDragEnd}
        onClick={() => addBlock(b.id)}
        title={`${b.description} — click or drag in. AI fills it for this email.`}
        style={{ position: 'relative', display: 'flex', gap: 10, alignItems: 'center', width: 248, minHeight: 78, boxSizing: 'border-box',
                 borderRadius: 16, padding: 10, cursor: 'grab', userSelect: 'none',
                 background: 'rgba(255,255,255,0.04)',
                 border: suggested ? '1.5px solid transparent' : '1px solid rgba(255,255,255,0.09)',
                 backgroundImage: suggested ? `linear-gradient(rgba(20,10,50,0.92),rgba(20,10,50,0.92)), ${SPEC}` : undefined,
                 backgroundOrigin: suggested ? 'border-box' : undefined, backgroundClip: suggested ? 'padding-box, border-box' : undefined,
                 boxShadow: suggested ? '0 0 22px rgba(124,92,246,0.45)' : 'none' }}>
        {suggested && <span data-testid={`studio-suggest-${b.id}`} style={{ position: 'absolute', top: -8, right: 8, background: SPEC, color: '#fff', fontFamily: 'Inter', fontSize: 9, fontWeight: 800, borderRadius: 999, padding: '2px 8px' }}>✦ suggested</span>}
        <Thumb id={b.id} group={b.group} />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12.5, fontWeight: 600, color: TXT }}>{b.name}</p>
          <p style={{ margin: '2px 0 0', fontFamily: 'Inter', fontSize: 10.5, color: SUB, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>{b.description}</p>
        </div>
      </div>
    );
  };

  // ── Custom tab panel — gradient text, brand style, structure, My Blocks ─
  const applyGradSel = () => {
    const { s, e } = gradSelRef.current;
    if (s === e) { showToast('Select the exact words in the text box first'); return; }
    setGradText(gradText.slice(0, s) + '==' + gradText.slice(s, e) + '==' + gradText.slice(e));
    gradSelRef.current = { s: 0, e: 0 };
    setGradFlash(true);
    setTimeout(() => setGradFlash(false), 700);
  };
  const gradPreview = (txt) => (txt || ' ').split(/(==[^=\n]+==)/g).filter(Boolean).map((part, i) =>
    part.startsWith('==') && part.endsWith('==')
      ? <span key={i} style={{ fontWeight: 800, backgroundImage: SPEC, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{part.slice(2, -2)}</span>
      : <span key={i}>{part}</span>);
  const customCard = { width: 248, boxSizing: 'border-box', borderRadius: 16, padding: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' };
  const customHead = (title, sub) => (<>
    <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, color: TXT }}>{title}</p>
    <p style={{ margin: '3px 0 9px', fontFamily: 'Inter', fontSize: 10.5, color: SUB, lineHeight: 1.5 }}>{sub}</p>
  </>);
  const customPanel = (
    <>
      <div data-testid="custom-gradient-tool" style={customCard}>
        {customHead('Gradient text', 'Select the exact words, hit spectrum. Casing never changes — clients without gradient support get solid brand violet, never invisible text.')}
        <textarea ref={gradRef} data-testid="custom-gradient-input" rows={3} value={gradText}
          onChange={(e) => setGradText(e.target.value)}
          onSelect={(e) => { gradSelRef.current = { s: e.target.selectionStart, e: e.target.selectionEnd }; }}
          style={{ ...inputS, fontSize: 12 }} />
        <div style={{ display: 'flex', gap: 5, margin: '7px 0', flexWrap: 'wrap' }}>
          <button data-testid="custom-gradient-apply" onClick={applyGradSel} style={{ ...mini, color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)', fontSize: 10.5 }}>✦ Spectrum on selection</button>
          <button data-testid="custom-gradient-clear" onClick={() => setGradText(gradText.replace(/==/g, ''))} style={{ ...mini, fontSize: 10.5 }}>Clear</button>
        </div>
        <div data-testid="custom-gradient-preview" className={`km-shimmer${gradFlash ? ' km-shimmer-run' : ''}`} style={{ borderRadius: 10, background: '#100838', padding: '10px 12px', fontFamily: HEAD, fontSize: 13, fontWeight: 700, color: '#EAF2FF', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{gradPreview(gradText)}</div>
        <div style={{ display: 'flex', gap: 5, marginTop: 9, flexWrap: 'wrap' }}>
          <button data-testid="custom-gradient-insert-line" onClick={() => addBlock('highlight_line', null, { text: gradText, size: 'medium' })} style={{ ...btn(SPEC), padding: '6px 13px', fontSize: 11 }}>Insert as highlight line</button>
          <button data-testid="custom-gradient-insert-para" onClick={() => addBlock('paragraph', null, { text: gradText })} style={{ ...mini, fontSize: 10.5 }}>as paragraph</button>
        </div>
      </div>

      <div data-testid="custom-brand-style" style={customCard}>
        {customHead('Brand style', 'Accent applied when you insert accent-aware blocks — callouts, guarantee badge, Leak Check hero, CTA fill.')}
        <div style={{ display: 'flex', gap: 6 }}>
          {ACCENTS.map((a) => (
            <button key={a.id} data-testid={`brand-style-${a.id}`} onClick={() => { setBrandAccent(a.id); showToast(`Brand accent: ${a.label}`); }}
              style={{ ...mini, display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', fontSize: 10.5,
                       border: brandAccent === a.id ? '1.5px solid rgba(0,229,195,0.7)' : mini.border,
                       background: brandAccent === a.id ? 'rgba(0,229,195,0.1)' : 'transparent' }}>
              <span style={{ width: 13, height: 13, borderRadius: '50%', background: a.swatch, display: 'inline-block' }} />{a.label}
            </button>
          ))}
        </div>
      </div>

      <div data-testid="custom-structure" style={customCard}>
        {customHead('Structure', 'Quick spacing and layout — dropped in as-is, no AI fill.')}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          <button data-testid="custom-insert-divider" onClick={() => addBlock('divider', null, { style: brandAccent === 'spectrum' ? 'spectrum' : 'line' })} style={{ ...mini, fontSize: 10.5 }}>— Divider</button>
          {['small', 'medium', 'large'].map((s) => (
            <button key={s} data-testid={`custom-insert-spacer-${s}`} onClick={() => addBlock('spacer', null, { size: s })} style={{ ...mini, fontSize: 10.5 }}>␣ {s[0].toUpperCase()}</button>
          ))}
          <button data-testid="custom-insert-callout" onClick={() => addBlock('callout_box')} style={{ ...mini, fontSize: 10.5 }}>▣ Callout</button>
        </div>
      </div>

      <div data-testid="custom-myblocks" style={customCard}>
        {customHead('My blocks', 'Your saved copy, reusable in one click. Select any block on the canvas and hit ★ to save it here.')}
        {myBlocks.length === 0 && <p data-testid="myblocks-empty" style={{ margin: 0, fontFamily: 'Inter', fontSize: 11, color: SUB, fontStyle: 'italic' }}>Nothing saved yet.</p>}
        {myBlocks.map((mb) => (
          <div key={mb.custom_id} data-testid={`myblock-${mb.custom_id}`} className="km-chip" style={{ display: 'flex', gap: 8, alignItems: 'center', borderRadius: 12, padding: 7, margin: '0 0 6px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div onClick={() => addBlock(mb.block_id, null, mb.fields || {})} data-testid={`myblock-insert-${mb.custom_id}`}
              title="Click to insert with your saved copy" style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 0, cursor: 'pointer' }}>
              <Thumb id={mb.block_id} group="custom" />
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 11.5, fontWeight: 700, color: TXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>★ {mb.name}</p>
                <p style={{ margin: '2px 0 0', fontFamily: 'Inter', fontSize: 9.5, color: SUB }}>{blockDef(mb.block_id)?.name || mb.block_id}</p>
              </div>
            </div>
            <button onClick={() => deleteMyBlock(mb)} data-testid={`myblock-delete-${mb.custom_id}`} style={{ ...mini, color: '#FF2E9A', padding: '2px 7px', fontSize: 10 }}>✕</button>
          </div>
        ))}
      </div>
    </>
  );

  const gallery = (
    <div data-testid="studio-left-rail" style={{ ...glass(20), width: 288, flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 16px 10px', flexShrink: 0 }}>
        <p style={{ fontFamily: HEAD, fontSize: 14, fontWeight: 700, color: TXT, margin: 0 }}>Block gallery</p>
        <p data-testid="studio-gallery-count" style={{ fontFamily: 'Inter', fontSize: 10.5, color: SUB, margin: '2px 0 10px' }}>{library?.blocks?.length || 0} blocks · {library?.groups?.length || 9} groups — see it before you drop it in</p>
        <button data-testid="open-icon-picker" onClick={() => openIconPicker(null)}
          style={{ ...mini, display: 'block', width: '100%', marginBottom: 8, padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#00E5C3', border: '1px solid rgba(0,229,195,0.35)', background: 'rgba(0,229,195,0.06)' }}>
          ✦ Icon library — search 1,995 icons + graphics
        </button>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {[{ id: 'all', name: 'All' }, ...CATS].map((c) => (
            <button key={c.id} data-testid={`studio-group-${c.id}`} onClick={() => { setCat(c.id); }}
              style={{ ...mini, padding: '4px 12px', border: cat === c.id ? 'none' : mini.border, background: cat === c.id ? SPEC : 'transparent', color: cat === c.id ? '#fff' : SUB }}>{c.id === 'custom' ? '★ Custom' : c.name}</button>
          ))}
        </div>
      </div>
      <div key={cat} className="studio-scroll km-stagger" style={{ flex: 1, overflowY: 'auto', minHeight: 0, overscrollBehavior: 'contain', padding: '4px 14px 16px', display: 'grid', gap: 8, alignContent: 'start' }}>
        {cat === 'custom' ? customPanel : galleryItems().map((it, n) => (
          it.type === 'header'
            ? <p key={`h${n}`} data-testid={`gallery-group-header-${n}`} style={{ margin: '8px 2px 0', fontFamily: 'Inter', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>{it.label}</p>
            : galleryCard(it.b)
        ))}
      </div>
    </div>
  );

  // ── CENTRE — light paper hero ──────────────────────────────────────────
  const DropLine = () => <div data-testid="composer-dropline" style={{ height: 3, borderRadius: 3, background: SPEC, margin: '3px 0', boxShadow: '0 0 10px rgba(196,72,240,0.8)' }} />;
  const blockCard = (b, i) => {
    const def = blockDef(b.block_id), selected = selIdx === i, html = frags?.block_htmls?.[i];
    return (
      <React.Fragment key={b._uid || `${b.block_id}-${i}`}>
        {dropIdx === i && <DropLine />}
        <div data-testid={`studio-block-${i}`} className="studio-block km-block-in" draggable={!selected && !lockedTpl}
          onDragStart={(e) => onBlockDragStart(e, i)} onDragOver={(e) => onBlockDragOver(e, i)} onDragEnd={onDragEnd}
          onClick={(e) => { e.stopPropagation(); if (lockedTpl) return; setHeaderOpen(false); setFooterOpen(false); setSelIdx(selected ? null : i); }}
          style={{ position: 'relative', zIndex: selected ? 60 : 'auto', outline: selected ? '2px solid transparent' : 'none', cursor: lockedTpl ? 'default' : selected ? 'default' : 'grab',
                   ...(selected ? { boxShadow: '0 0 0 2px #7C5CF6, 0 0 18px rgba(124,92,246,0.5)' } : {}) }}>
          {(selected || undefined) && (
            <div style={{ position: 'absolute', top: -34, left: '50%', transform: 'translateX(-50%)', zIndex: 6, display: 'flex', gap: 2, ...glass(999), padding: '4px 8px', background: 'rgba(7,3,19,0.9)' }} onClick={(e) => e.stopPropagation()}>
              <button onClick={() => regenBlock(i, 'Fresh take: keep the intent and length, but change the wording noticeably.')} disabled={regenIdx !== null} style={{ ...mini, border: 'none', color: '#00E5C3', padding: '2px 8px' }} data-testid={`studio-block-ai-${i}`}>{regenIdx === i ? '✦ Rewriting…' : '✦ Rewrite'}</button>
              <button onClick={() => move(i, -1)} disabled={i === 0} style={{ ...mini, border: 'none', padding: '2px 6px', opacity: i === 0 ? 0.3 : 1 }} data-testid={`composer-block-up-${i}`}><ArrowUp size={12} /></button>
              <button onClick={() => move(i, 1)} disabled={i === blocks.length - 1} style={{ ...mini, border: 'none', padding: '2px 6px', opacity: i === blocks.length - 1 ? 0.3 : 1 }} data-testid={`composer-block-down-${i}`}><ArrowDown size={12} /></button>
              <button onClick={() => duplicateBlock(i)} style={{ ...mini, border: 'none', padding: '2px 6px' }} data-testid={`studio-block-dup-${i}`}><Copy size={12} /></button>
              <button onClick={(e) => saveMyBlock(i, e.currentTarget)} title="Save to My Blocks (Custom tab)" style={{ ...mini, border: 'none', color: '#F0C36D', padding: '2px 6px' }} data-testid={`studio-block-save-${i}`}>★</button>
              <button onClick={() => removeBlock(i)} style={{ ...mini, border: 'none', color: '#FF2E9A', padding: '2px 6px' }} data-testid={`studio-block-del-${i}`}><X size={13} /></button>
            </div>
          )}
          {fillingIdx === i ? (
            <div style={{ background: '#100838', padding: '26px 40px', color: '#00E5C3', fontFamily: 'Inter', fontSize: 12.5, textAlign: 'center' }}>✦ AI is writing this block…</div>
          ) : html !== undefined ? <div dangerouslySetInnerHTML={{ __html: html }} />
            : <div style={{ background: '#100838', padding: '20px 40px', color: SUB, fontFamily: 'Inter', fontSize: 12 }}>{def?.name || b.block_id}…</div>}
          {selected && def && (
            <div style={popover} onClick={(e) => e.stopPropagation()} data-testid={`studio-block-settings-${i}`}>
              <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px' }}>
                <p style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 800, color: '#00E5C3', margin: 0, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{def.name} — settings</p>
                <button onClick={() => setSelIdx(null)} data-testid={`studio-block-settings-done-${i}`}
                  style={{ ...btn(SPEC), marginLeft: 'auto', padding: '4px 14px', fontSize: 11 }}>Done</button>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {(def.id === 'icon_badge' || def.id === 'spot_graphic') && (
                  <button data-testid={`studio-swap-icon-${i}`}
                    onClick={() => openIconPicker({ idx: i, field: def.id === 'icon_badge' ? 'icon' : 'name' })}
                    style={{ ...mini, padding: '7px 12px', color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)', textAlign: 'center', fontWeight: 700 }}>
                    ✦ Swap {def.id === 'icon_badge' ? 'icon' : 'graphic'} — open library
                  </button>
                )}
                {['icon_feature_row', 'icon_strip'].includes(def.id) && (b.fields?.items || []).map((_, ii) => (
                  <button key={ii} data-testid={`studio-swap-icon-${i}-item-${ii}`}
                    onClick={() => openIconPicker({ idx: i, field: 'items', itemIdx: ii })}
                    style={{ ...mini, padding: '6px 12px', color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)', textAlign: 'center' }}>
                    ✦ Swap icon {ii + 1}
                  </button>
                ))}
                {!['icon_badge', 'icon_feature_row', 'icon_strip', 'spot_graphic'].includes(def.id) && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button data-testid={`studio-block-icon-slot-${i}`}
                      onClick={() => openIconPicker({ idx: i, field: 'icon' })}
                      style={{ ...mini, flex: 1, padding: '6px 12px', color: '#B9A6FF', border: '1px solid rgba(124,92,246,0.4)', textAlign: 'center' }}>
                      {b.fields?.icon ? `✦ Icon: ${b.fields.icon} — swap` : '＋ Add icon to this block'}
                    </button>
                    {b.fields?.icon && (
                      <button data-testid={`studio-block-icon-remove-${i}`}
                        onClick={() => { pushSnap(); setBlockField(i, 'icon', ''); setBlockField(i, 'icon_variant', ''); showToast('Icon removed'); }}
                        style={{ ...mini, padding: '6px 10px', color: '#FF7DB3', border: '1px solid rgba(255,125,179,0.35)' }}>✕</button>
                    )}
                  </div>
                )}
                {PROSE_BLOCKS.has(def.id) && !b.fields?.icon && blockText(b).length > 170 && (
                  <button data-testid={`studio-anchor-suggest-${i}`}
                    onClick={() => { pushSnap(); setBlockField(i, 'icon', pickAnchorIcon(blockText(b))); showToast('⚓ Anchor added — rhythm rebalanced'); }}
                    style={{ ...mini, padding: '7px 12px', color: '#F0C36D', border: '1px solid rgba(240,195,109,0.45)', textAlign: 'left', lineHeight: 1.45 }}>
                    ⚓ Long text run — anchor it (adds a matched icon beside this block so the rhythm survives)
                  </button>
                )}
                {def.fields.map((f) => {
                  const val = (b.fields || {})[f.key] !== undefined ? b.fields[f.key] : f.sample;
                  if (f.type === 'list') return <textarea key={f.key} data-testid={`composer-field-${f.key}`} rows={3} value={(Array.isArray(val) ? val : []).join('\n')} onChange={(e) => setBlockField(i, f.key, e.target.value.split('\n'))} style={inputS} />;
                  if (f.type === 'textarea') return <textarea key={f.key} data-testid={`composer-field-${f.key}`} rows={3} value={val || ''} onChange={(e) => setBlockField(i, f.key, e.target.value)} style={inputS} />;
                  return <input key={f.key} data-testid={`composer-field-${f.key}`} value={val || ''} onChange={(e) => setBlockField(i, f.key, e.target.value)} style={inputS} placeholder={f.label} />;
                })}
              </div>
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  const genIndicator = chatBusy && (
    <div data-testid="studio-generating" style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', margin: '14px 0 10px' }}>
      <span className="genbar" style={{ width: 110, height: 3, borderRadius: 999, background: 'linear-gradient(90deg,#00E5C3,#38B6FF,#C448F0,#FF2E9A,#00E5C3)', backgroundSize: '200% 100%' }} />
      <span key={genStage || 'w'} className="km-stage-in" style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 700, color: '#38B6FF' }}>{genStage || 'Working…'}</span>
    </div>
  );

  const recipientSet = !!(sendForm.first_name || sendForm.business_name || sendForm.to_email || linkedLead);
  const emptyState = (
    <div data-testid="studio-empty-state" style={{ maxWidth: 560, margin: '5vh auto 0', textAlign: 'center' }}>
      <div style={{ width: 46, height: 4, borderRadius: 3, background: SPEC, margin: '0 auto 24px' }} />
      <h2 style={{ fontFamily: HEAD, fontSize: 26, fontWeight: 700, color: TXT, margin: '0 0 8px', letterSpacing: '-0.5px' }}>
        What are we sending{sendForm.first_name ? ` ${sendForm.first_name}` : ''} today?
      </h2>
      <p style={{ fontFamily: 'Inter', fontSize: 13.5, color: SUB, margin: '0 0 20px', lineHeight: 1.65 }}>
        Describe it in a sentence — the AI drafts the whole email, on brand, personalised to {sendForm.business_name || 'the recipient'}.
      </p>
      {/* RECIPIENT FIRST — who's it for, BEFORE composing */}
      <div data-testid="studio-recipient-setup" style={{ ...glass(16), padding: '12px 14px', margin: '0 0 14px', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 9, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: SUB }}>Who's it for?</p>
          {recipientSet && <span data-testid="recipient-attached-chip" style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 700, color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)', borderRadius: 999, padding: '1px 9px' }}>{linkedLead ? '🔗 lead linked' : '✓ recipient set'}</span>}
          {oneOff && !recipientSet && <span data-testid="oneoff-chip" style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 700, color: '#F0C36D', border: '1px solid rgba(240,195,109,0.4)', borderRadius: 999, padding: '1px 9px' }}>one-off — no lead</span>}
          {!recipientSet && !oneOff && (
            <button data-testid="no-lead-oneoff" onClick={() => setBriefMention(null) || setOneOff(true)}
              style={{ ...mini, marginLeft: 'auto', padding: '2px 10px', fontSize: 10.5 }}>No lead — one-off →</button>
          )}
          {oneOff && <button data-testid="oneoff-undo" onClick={() => setOneOff(false)} style={{ ...mini, marginLeft: 'auto', padding: '2px 10px', fontSize: 10.5 }}>↩ pick a lead</button>}
        </div>
        {!oneOff && (
          <>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <input data-testid="lead-search-input" placeholder="🔍 Search existing leads…" value={leadQ}
                onChange={(e) => setLeadQ(e.target.value)} disabled={chatBusy}
                style={{ ...inputS, width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 12.5 }} />
              {leadResults.length > 0 && (
                <div data-testid="lead-search-results" style={{ position: 'absolute', top: '105%', left: 0, right: 0, zIndex: 30, background: '#140A33', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 30px rgba(0,0,0,0.5)' }}>
                  {leadResults.map((l) => (
                    <button key={l.lead_id} data-testid={`lead-result-${l.lead_id}`}
                      onClick={() => { attachLead(l, `🔗 Lead linked: ${l.first_name || l.business_name}`); setLeadQ(''); setLeadResults([]); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '8px 12px', cursor: 'pointer' }}>
                      <span style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, color: TXT }}>{l.first_name || '—'}{l.business_name ? ` · ${l.business_name}` : ''}</span>
                      <span style={{ display: 'block', fontFamily: 'Inter', fontSize: 10.5, color: SUB }}>{l.email || 'no email'}{l.stage ? ` · ${l.stage}` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input data-testid="quickadd-firstname" placeholder="First name" value={sendForm.first_name}
                onChange={(e) => setSendForm({ ...sendForm, first_name: e.target.value })} disabled={chatBusy}
                style={{ ...inputS, flex: 1, minWidth: 0, padding: '8px 11px', fontSize: 12 }} />
              <input data-testid="quickadd-business" placeholder="Business" value={sendForm.business_name}
                onChange={(e) => setSendForm({ ...sendForm, business_name: e.target.value })} disabled={chatBusy}
                style={{ ...inputS, flex: 1.2, minWidth: 0, padding: '8px 11px', fontSize: 12 }} />
              <input data-testid="quickadd-email" placeholder="email@… (optional)" value={sendForm.to_email}
                onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })} disabled={chatBusy}
                style={{ ...inputS, flex: 1.3, minWidth: 0, padding: '8px 11px', fontSize: 12 }} />
            </div>
            <textarea data-testid="lead-notes-input" rows={2} value={leadNotes} disabled={chatBusy}
              placeholder="Anything I should know? — how you met, their situation, what they care about (the AI composes from this)"
              onChange={(e) => setLeadNotes(e.target.value)} onBlur={() => { if (linkedLead?.lead_id && leadNotes !== (linkedLead.notes || '')) saveLeadNotes(); }}
              style={{ ...inputS, width: '100%', boxSizing: 'border-box', padding: '8px 11px', fontSize: 12, resize: 'vertical', fontFamily: 'Inter', lineHeight: 1.5 }} />
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input data-testid="studio-hero-input" placeholder='e.g. "warm email about missed calls, one stat, Leak Check offer"'
          value={heroInput} onChange={(e) => setHeroInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') draftFromHero(); }}
          disabled={chatBusy || scanBusy} style={{ ...inputS, flex: 1, padding: '14px 18px', fontSize: 14, borderRadius: 14 }} />
        <button data-testid="studio-hero-go" onClick={draftFromHero} disabled={chatBusy || scanBusy || !heroInput.trim()}
          style={{ ...btn(SPEC), padding: '0 22px', borderRadius: 14, opacity: chatBusy || scanBusy || !heroInput.trim() ? 0.5 : 1 }}>{chatBusy || scanBusy ? '…' : 'Draft it'}</button>
      </div>
      {scanBusy && (
        <p data-testid="brief-scan-indicator" style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 700, color: '#38B6FF', margin: '10px 0 0' }}>Checking who it's for…</p>
      )}
      {briefMention && !chatBusy && (
        <div data-testid="brief-mention-prompt" style={{ ...glass(14), padding: '12px 14px', margin: '12px 0 0', textAlign: 'left' }}>
          <p style={{ margin: '0 0 8px', fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, color: TXT }}>Who's this for? Your brief mentions someone not attached yet:</p>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <button data-testid="brief-mention-create" onClick={createFromMention}
              style={{ ...mini, padding: '7px 13px', color: '#00E5C3', border: '1px solid rgba(0,229,195,0.45)', fontWeight: 700 }}>
              ＋ Create lead: {briefMention.first_name || '?'}{briefMention.business_name ? ` · ${briefMention.business_name}` : ''} & compose
            </button>
            <button data-testid="brief-mention-skip" onClick={() => { setBriefMention(null); setOneOff(true); sendChat(heroInput); }}
              style={{ ...mini, padding: '7px 13px' }}>No lead — compose one-off</button>
          </div>
        </div>
      )}
      {genIndicator}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 18, flexWrap: 'wrap' }}>
        {STARTERS.map((s, n) => <button key={s.label} data-testid={`studio-starter-${n}`} onClick={() => sendChat(s.prompt)} disabled={chatBusy} style={{ ...mini, padding: '7px 15px', fontSize: 12 }}>{s.label}</button>)}
      </div>
      {templates.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <p style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: SUB, margin: '0 0 10px' }}>or load a saved template</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
            {templates.slice(0, 8).map((t) => (
              <span key={t.template_id} style={{ display: 'inline-flex', gap: 3 }}>
                <button data-testid={`composer-template-${t.template_id}`} onClick={() => loadTemplate(t)} style={mini}>{t.locked ? '🔒 ' : ''}{t.name}</button>
                {!t.locked && <button onClick={() => deleteTemplate(t)} style={{ ...mini, color: '#FF2E9A', padding: '4px 7px' }}>✕</button>}
              </span>
            ))}
            {templates.length > 8 && <span style={{ ...mini, border: 'none', color: SUB }}>+{templates.length - 8} more in Templates…</span>}
          </div>
        </div>
      )}
    </div>
  );

  const canvas = (
    <div data-testid="studio-canvas" onClick={closeEditors} onDragOver={onCanvasDragOver} onDrop={onCanvasDrop} className="studio-scroll"
      style={{ flex: 1, overflowY: 'auto', minHeight: 0, minWidth: 0, overscrollBehavior: 'contain', padding: wide ? '4px 26px 110px' : '4px 10px 130px' }}>
      {!hasDraft ? emptyState : (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          {genIndicator}
          {lockedTpl && (
            <div data-testid="locked-template-banner" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 12px', borderRadius: 12, padding: '9px 14px', background: 'rgba(240,195,109,0.1)', border: '1px solid rgba(240,195,109,0.4)', fontFamily: 'Inter', fontSize: 11.5, color: '#F0C36D', fontWeight: 700 }}>
              🔒 Locked template — only {'{{first_name}}'} and {'{{business_name}}'} merge per recipient. Content can't be edited.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 14px', ...(chatBusy ? { pointerEvents: 'none', opacity: 0.6 } : {}) }}>
            <div data-testid="studio-recipient-bar" style={{ ...glass(999), display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px' }}>
              <span style={{ width: 20, height: 20, borderRadius: '50%', background: SPEC, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: HEAD, fontSize: 10, fontWeight: 700 }}>{(sendForm.first_name || '?')[0]}</span>
              <input data-testid="studio-recipient-firstname" value={sendForm.first_name} placeholder="first name" onChange={(e) => setSendForm({ ...sendForm, first_name: e.target.value })} style={{ ...quiet, width: 80, borderBottom: 'none', fontWeight: 600 }} />
              <span style={{ color: SUB }}>·</span>
              <input data-testid="studio-recipient-business" value={sendForm.business_name} placeholder="business" onChange={(e) => setSendForm({ ...sendForm, business_name: e.target.value })} style={{ ...quiet, width: 110, borderBottom: 'none' }} />
              <input data-testid="studio-recipient-email" value={sendForm.to_email} placeholder="email@…" onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })} style={{ ...quiet, width: 170, borderBottom: 'none', color: SUB, fontSize: 11.5 }} />
              {linkedLead?.stage && <span style={{ fontFamily: 'Inter', fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)', borderRadius: 999, padding: '2px 8px' }}>{linkedLead.stage}</span>}
            </div>
            <div style={{ ...glass(999), display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', flex: 1, minWidth: 200 }}>
              <span style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: SUB }}>Subj</span>
              <input data-testid="composer-subject" value={subject} readOnly={lockedTpl} onChange={(e) => { if (lockedTpl) return; setSubject(e.target.value); setTouchedSubject(true); }} placeholder="Subject…" style={{ ...quiet, flex: 1, borderBottom: 'none', fontWeight: 600, fontSize: 13, ...(lockedTpl ? { opacity: 0.75 } : {}) }} />
              {!lockedTpl && (
              <button data-testid="subject-suggest-btn" onClick={suggestSubjects} disabled={subjBusy} title="AI subject ideas — 3 ranked options"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#C448F0', fontFamily: 'Inter', fontSize: 13, fontWeight: 800, padding: '0 2px' }}>{subjBusy ? '…' : '✦'}</button>
              )}
              {lockedTpl && <span title="Locked template" style={{ fontSize: 12 }}>🔒</span>}
            </div>
          </div>

          {subjectSuggs && (
            <div data-testid="subject-suggestions" style={{ ...glass(14), padding: '10px 12px', margin: '0 0 14px' }}>
              <p style={{ margin: '0 0 7px', fontFamily: 'Inter', fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>Subject ideas — tap to apply</p>
              {subjectSuggs.map((s, n) => (
                <button key={n} data-testid={`subject-sugg-${n}`} onClick={() => { setSubject(s.subject); setTouchedSubject(true); setSubjectSuggs(null); showToast('✓ Subject applied'); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 11px', margin: '0 0 6px', cursor: 'pointer' }}>
                  <span style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, color: TXT }}>{s.subject}</span>
                  <span style={{ display: 'block', fontFamily: 'Inter', fontSize: 10.5, marginTop: 2 }}>
                    <span style={{ color: '#00E5C3', fontWeight: 700 }}>{s.style}</span>
                    <span style={{ color: SUB }}> — {s.reason}</span>
                  </span>
                </button>
              ))}
              <div style={{ display: 'flex', gap: 6 }}>
                <button data-testid="subject-sugg-regen" onClick={suggestSubjects} disabled={subjBusy} style={mini}>↻ More ideas</button>
                <button data-testid="subject-sugg-close" onClick={() => setSubjectSuggs(null)} style={mini}>✕</button>
              </div>
            </div>
          )}

          {mode === 'preview' && (
            <>
              <div data-testid="preview-email-safe-note" style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', margin: '0 0 10px', fontFamily: 'Inter', fontSize: 11.5, color: SUB }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#00E5C3' }} />
                Exact email-safe render — what recipients get. Table-based, navy surfaces, gradients ship as solid violet fallback.
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', margin: '0 0 14px' }}>
                <button data-testid="preview-test-send-btn" onClick={() => sendTest()} disabled={testBusy}
                  className={testBusy ? 'km-busy-pulse' : testSent ? 'km-sent-ok' : ''}
                  title={`Sends this exact email-safe HTML to ${TEST_INBOX} — [TEST] subject, gated + logged`}
                  style={{ ...mini, padding: '7px 16px', fontSize: 11.5, color: '#00E5C3', border: '1px solid rgba(0,229,195,0.45)', opacity: testBusy ? 0.7 : 1 }}>
                  {testBusy ? '✈ Sending test…' : testSent ? '✓ Test sent — check the inbox' : `✈ Send test to myself — ${TEST_INBOX}`}
                </button>
              </div>
              {(() => {
                const rows = blocks.map((b) => blockDef(b.block_id)).filter((d) => d?.scorecard);
                const CH = { apple: 'Apple Mail', gmail: 'Gmail', outlook: 'Outlook', images_off: 'Images off' };
                return (
                  <div data-testid="motion-scorecard" style={{ maxWidth: 640, margin: '0 auto 16px', display: 'grid', gap: 6 }}>
                    <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB, textAlign: 'center' }}>Rendering scorecard — what each client gets</p>
                    <div data-testid="scorecard-summary" style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', justifyContent: 'center', borderRadius: 12, padding: '7px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
                      <span style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 800, color: TXT }}>Static email-safe</span>
                      {['Apple Mail ✓', 'Gmail ✓', 'Outlook ✓', 'Images off ✓ text intact'].map((c) => (
                        <span key={c} style={{ fontFamily: 'Inter', fontSize: 9.5, fontWeight: 700, borderRadius: 999, padding: '2px 9px', color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)' }}>{c}</span>
                      ))}
                    </div>
                    {rows.map((d, n) => (
                      <div key={`${d.id}-${n}`} data-testid={`scorecard-${d.id}`} style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', justifyContent: 'center', borderRadius: 12, padding: '7px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
                        <span style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 800, color: TXT }}>{d.name}</span>
                        {Object.entries(CH).map(([k, label]) => (
                          <span key={k} style={{ fontFamily: 'Inter', fontSize: 9.5, fontWeight: 700, borderRadius: 999, padding: '2px 9px', color: '#8FA0C8', border: '1px solid rgba(255,255,255,0.16)' }}>
                            {label} {d.scorecard[k]}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          )}

          {mode === 'preview' ? (
            <div style={{ borderRadius: 20, overflow: 'hidden', boxShadow: '0 40px 100px rgba(0,0,0,0.6)' }}>
              {emailPreviewHtml
                ? <iframe title="email-safe preview" srcDoc={emailPreviewHtml} data-testid="email-safe-preview-frame"
                    style={{ width: '100%', height: '72vh', border: 'none', display: 'block', background: '#0B0526' }} sandbox="" />
                : <div style={{ background: '#100838', padding: 60, textAlign: 'center', color: SUB, fontFamily: 'Inter', fontSize: 12.5 }}>Rendering the real email…</div>}
            </div>
          ) : (
          <div style={{ background: '#FAFBFF', borderRadius: 20, overflow: 'visible', boxShadow: '0 40px 100px rgba(0,0,0,0.6), 0 0 80px rgba(124,92,246,0.18)' }}>
            <div style={{ height: 4, borderRadius: '20px 20px 0 0', background: SPEC }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px' }}>
              <span style={{ width: 18, height: 18, borderRadius: 5, background: SPEC, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: HEAD, fontSize: 10, fontWeight: 800 }}>K</span>
              <span style={{ fontFamily: HEAD, fontSize: 12, fontWeight: 700, color: INK }}>Kindra AI</span>
              <span style={{ fontFamily: 'Inter', fontSize: 10.5, color: INK_SUB, marginLeft: 'auto' }}>kindraai.dev</span>
            </div>
            <div style={{ padding: '0 14px 14px', ...(chatBusy ? { pointerEvents: 'none', opacity: 0.72, filter: 'saturate(0.85)' } : {}) }}>
              <div style={{ borderRadius: 14, overflow: 'visible' }}>
                <div data-testid="composer-locked-header" onClick={(e) => { e.stopPropagation(); if (lockedTpl) return; setSelIdx(null); setFooterOpen(false); setHeaderOpen(!headerOpen); }} title={lockedTpl ? 'Locked template — content cannot be edited' : 'Locked A-frame header — click to edit copy'} style={{ position: 'relative', cursor: lockedTpl ? 'default' : 'pointer' }}>
                  <span style={{ position: 'absolute', top: 8, right: 8, zIndex: 4, fontFamily: 'Inter', fontSize: 9, fontWeight: 700, color: SUB, background: 'rgba(7,3,19,0.8)', borderRadius: 999, padding: '2px 9px' }}>🔒 locked · ✎</span>
                  {frags?.header_html ? <div dangerouslySetInnerHTML={{ __html: frags.header_html }} /> : <div style={{ background: '#100838', padding: 40, color: SUB, fontFamily: 'Inter', fontSize: 12, textAlign: 'center' }}>Rendering…</div>}
                  {headerOpen && (
                    <div style={popover} onClick={(e) => e.stopPropagation()} data-testid="composer-header-editor">
                      <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px' }}>
                        <p style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 800, color: '#00E5C3', margin: 0, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Header copy</p>
                        <button onClick={() => setHeaderOpen(false)} data-testid="composer-header-editor-done"
                          style={{ ...btn(SPEC), marginLeft: 'auto', padding: '4px 14px', fontSize: 11 }}>Done</button>
                      </div>
                      {['eyebrow', 'h1'].map((k) => frameField(k))}{frameField('intro', true)}
                    </div>
                  )}
                </div>
                <div data-testid="composer-blocks">
                  {blocks.length === 0 && <div style={{ background: '#100838', padding: '28px 20px', textAlign: 'center', color: SUB, fontFamily: 'Inter', fontSize: 12.5 }}>No blocks — ask the co-writer, or pick from the gallery.</div>}
                  {blocks.map(blockCard)}
                  {chatBusy && <div data-testid="studio-skeleton-block" className="genskel" style={{ height: 62, margin: '4px 0', background: 'linear-gradient(90deg, #100838 25%, #1B0F4E 50%, #100838 75%)', backgroundSize: '200% 100%' }} />}
                  {dropIdx === blocks.length && blocks.length > 0 && <DropLine />}
                </div>
                <div data-testid="composer-locked-footer" onClick={(e) => { e.stopPropagation(); if (lockedTpl) return; setSelIdx(null); setHeaderOpen(false); setFooterOpen(!footerOpen); }} title={lockedTpl ? 'Locked template — content cannot be edited' : 'Locked CTA · sign-off · compliant footer — click to edit CTA'} style={{ position: 'relative', cursor: lockedTpl ? 'default' : 'pointer' }}>
                  <span style={{ position: 'absolute', top: 8, right: 8, zIndex: 4, fontFamily: 'Inter', fontSize: 9, fontWeight: 700, color: SUB, background: 'rgba(7,3,19,0.8)', borderRadius: 999, padding: '2px 9px' }}>🔒 locked · ✎ CTA</span>
                  {frags?.footer_html ? <div dangerouslySetInnerHTML={{ __html: frags.footer_html }} /> : <div style={{ background: '#100838', padding: 40, color: SUB, fontFamily: 'Inter', fontSize: 12, textAlign: 'center' }}>Rendering…</div>}
                  {footerOpen && (
                    <div style={{ ...popover, top: 'auto', bottom: 'calc(100% + 8px)' }} onClick={(e) => e.stopPropagation()} data-testid="composer-footer-editor">
                      <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px' }}>
                        <p style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 800, color: '#00E5C3', margin: 0, textTransform: 'uppercase', letterSpacing: '0.12em' }}>CTA copy</p>
                        <button onClick={() => setFooterOpen(false)} data-testid="composer-footer-editor-done"
                          style={{ ...btn(SPEC), marginLeft: 'auto', padding: '4px 14px', fontSize: 11 }}>Done</button>
                      </div>
                      {['cta_label', 'cta_url'].map((k) => frameField(k))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          )}

          {pacing && (
            <div data-testid="studio-warmup-chip" style={{ ...glass(999), display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 16px', margin: '16px auto 0', fontFamily: 'Inter', fontSize: 11.5, color: SUB }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: pacing.remaining > 0 ? '#00E5C3' : '#F0C36D' }} />
              Warm-up {pacing.sent_today}/{pacing.cap} today · {pacing.remaining > 0 ? 'on pace' : 'cap reached — will ask before exceeding'}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── RIGHT island — co-writer ───────────────────────────────────────────
  const contextCard = (sendForm.first_name || linkedLead) && (
    <div data-testid="studio-context-card" style={{ margin: '4px 16px 8px', borderRadius: 14, padding: '10px 13px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <p style={{ margin: '0 0 6px', fontFamily: 'Inter', fontSize: 9, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: SUB }}>Using context</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 24, height: 24, borderRadius: '50%', background: SPEC, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: HEAD, fontSize: 11, fontWeight: 700 }}>{(sendForm.first_name || '?')[0]}</span>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: TXT }}>{sendForm.first_name || '—'} · {sendForm.business_name || '—'}</p>
          <p style={{ margin: '2px 0 0', fontFamily: 'Inter', fontSize: 10, color: SUB, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {linkedLead?.stage ? <span>stage: {linkedLead.stage}</span> : null}
            <NeonStatus state={leadStatus({ ...(linkedLead || {}), latest_send: lastSend })} pinned testId="context-neon-status" />
            <span>{lastSend ? `last sent ${(lastSend.sent_at || '').slice(0, 10)}` : 'no sends yet'}</span>
          </p>
        </div>
      </div>
      {(leadNotes || '').trim() && (
        <p data-testid="context-notes" style={{ margin: '7px 0 0', fontFamily: 'Inter', fontSize: 10.5, color: '#F0C36D', lineHeight: 1.5 }}>
          📝 <b>notes:</b> {leadNotes.slice(0, 150)}{leadNotes.length > 150 ? '…' : ''}
        </p>
      )}
      {proofs.length > 0 && bestProof() && (
        <p data-testid="context-proof-match" style={{ margin: '7px 0 0', fontFamily: 'Inter', fontSize: 10, color: '#00E5C3' }}>
          best proof match: <b>{bestProof().stat}</b> — {(bestProof().display || bestProof().caption || '').slice(0, 72)}
          {bestProof().kind === 'case' && <span style={{ color: SUB }}> · real client result (anonymised)</span>}
        </p>
      )}
    </div>
  );

  const chatPanel = (
    <div data-testid="studio-chat-panel" style={{ ...glass(20), width: wide ? 344 : '100%', flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
        <span className={chatBusy ? 'km-orb-think' : orbSettle ? 'km-orb-settle' : ''} style={{ width: 28, height: 28, borderRadius: '50%', background: SPEC, boxShadow: '0 0 16px rgba(196,72,240,0.55)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Sparkles size={13} color="#fff" /></span>
        <p style={{ fontFamily: HEAD, fontSize: 14, fontWeight: 700, color: TXT, margin: 0 }}>Co-writer</p>
        <span style={{ background: SPEC, color: '#fff', fontFamily: 'Inter', fontSize: 9, fontWeight: 800, borderRadius: 999, padding: '2px 9px' }}>✦ Kindra AI</span>
        <span className="live-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: '#00E5C3', marginLeft: 'auto' }} />
      </div>
      {contextCard}
      <div className="studio-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0, overscrollBehavior: 'contain', padding: '4px 16px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {chat.length === 0 && (
          <p style={{ fontFamily: 'Inter', fontSize: 12.5, color: SUB, lineHeight: 1.7 }}>
            Talk to me like you'd brief a copywriter.<br /><br />
            <em>"shorten para 2" · "soften the CTA" · "swap the stat" · "add a stat about missed calls"</em>
          </p>
        )}
        {chat.map((m, i) => (
          <div key={i} data-testid={`studio-chat-msg-${i}`} className="km-chat-in"
            style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '90%',
                     background: m.role === 'user' ? 'linear-gradient(95deg,rgba(56,182,255,0.22),rgba(124,92,246,0.22))' : 'rgba(255,255,255,0.05)',
                     border: '1px solid rgba(255,255,255,0.08)',
                     borderRadius: m.role === 'user' ? '13px 13px 4px 13px' : '13px 13px 13px 4px',
                     padding: '9px 13px', fontFamily: 'Inter', fontSize: 12.5, color: TXT, lineHeight: 1.6 }}>
            {m.content}
            {(m.action || m.action2 || m.depth) && (
              <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                {m.action && <span style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700, color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)', borderRadius: 999, padding: '2px 9px' }}>{m.action}</span>}
                {m.depth && DEPTH_META[m.depth] && (
                  <button data-testid={`studio-depth-chip-${i}`}
                    onClick={() => { if (i === chat.length - 1 && !chatBusy) setDepthPick(!depthPick); }}
                    title="Depth dial — the AI's auto-pick. Tap to recompose the same content at another depth."
                    style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700, color: '#B9A6FF', border: '1px solid rgba(124,92,246,0.5)', borderRadius: 999, padding: '2px 9px', background: 'rgba(124,92,246,0.1)', cursor: i === chat.length - 1 ? 'pointer' : 'default' }}>
                    {DEPTH_META[m.depth].sym} {DEPTH_META[m.depth].label}{i === chat.length - 1 ? ' — tap to change' : ''}
                  </button>
                )}
                {m.archetype && <span data-testid={`studio-archetype-chip-${i}`} title="Story archetype — the skeleton the AI derived from this brief's narrative" style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700, color: '#F0C36D', border: '1px solid rgba(240,195,109,0.4)', borderRadius: 999, padding: '2px 9px', textTransform: 'capitalize' }}>✦ {m.archetype}</span>}
                {m.action2 && <span data-testid={`studio-lead-linked-${i}`} style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700, color: '#38B6FF', border: '1px solid rgba(56,182,255,0.4)', borderRadius: 999, padding: '2px 9px' }}>{m.action2}</span>}
                {m.snapIdx !== undefined && <button data-testid={`studio-undo-${i}`} onClick={() => undo(m.snapIdx)} style={{ ...mini, padding: '2px 9px', fontSize: 10.5 }}>Undo</button>}
              </div>
            )}
            {m.depth && depthPick && i === chat.length - 1 && !chatBusy && (
              <div data-testid="studio-depth-options" style={{ display: 'grid', gap: 5, marginTop: 7 }}>
                {Object.entries(DEPTH_META).map(([id, dm]) => (
                  <button key={id} data-testid={`studio-depth-${id}`} disabled={id === m.depth}
                    onClick={() => recomposeAtDepth(id)}
                    style={{ ...mini, display: 'flex', alignItems: 'center', gap: 7, padding: '6px 11px', textAlign: 'left',
                             border: id === m.depth ? '1px solid rgba(124,92,246,0.7)' : mini.border,
                             background: id === m.depth ? 'rgba(124,92,246,0.18)' : 'transparent',
                             opacity: id === m.depth ? 0.75 : 1, cursor: id === m.depth ? 'default' : 'pointer' }}>
                    <span style={{ fontSize: 12 }}>{dm.sym}</span>
                    <span style={{ fontWeight: 700, color: TXT, fontSize: 11.5 }}>{dm.label}</span>
                    <span style={{ color: SUB, fontSize: 10 }}>{dm.hint}{id === m.depth ? ' · current' : ''}</span>
                  </button>
                ))}
              </div>
            )}
            {m.leadSuggest && (
              <div style={{ marginTop: 7 }}>
                <button data-testid={`studio-create-lead-${i}`} onClick={() => createLead(m.leadSuggest, i)}
                  style={{ ...mini, border: '1px solid rgba(0,229,195,0.45)', color: '#00E5C3', fontSize: 11, padding: '4px 12px' }}>
                  ＋ Create lead: {m.leadSuggest.first_name || '?'}{m.leadSuggest.business_name ? ` · ${m.leadSuggest.business_name}` : ''}
                </button>
              </div>
            )}
            {m.role === 'assistant' && i === chat.length - 1 && !chatBusy && suggests.length > 0 && hasDraft && (
              <div data-testid="studio-suggestions" style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
                {suggests.map((id) => blockDef(id) && (
                  <button key={id} onClick={() => addBlock(id)} style={{ ...mini, border: '1px solid rgba(0,229,195,0.35)', color: '#00E5C3', fontSize: 10.5, padding: '2px 9px' }}>✦ Insert {blockDef(id).name}</button>
                ))}
              </div>
            )}
          </div>
        ))}
        {chatBusy && (
          <div data-testid="studio-chat-progress" style={{ alignSelf: 'flex-start', background: 'rgba(255,255,255,0.05)', borderRadius: '13px 13px 13px 4px', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            {[0, 1, 2].map((n) => <span key={n} className="tdot" style={{ width: 6, height: 6, borderRadius: '50%', background: SUB, animationDelay: `${n * 0.18}s` }} />)}
            {genStage && <span key={genStage} className="km-stage-in" style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 600, color: '#38B6FF' }}>{genStage}</span>}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      <div style={{ padding: '8px 14px 14px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          {CHAT_CHIPS.map((c, n) => (
            <button key={c} data-testid={`studio-chip-${n}`} onClick={() => toneChip(c)} disabled={chatBusy}
              title={selIdx !== null ? 'applies to the selected block' : 'applies to the whole draft'}
              style={{ ...mini, border: '1px solid rgba(0,229,195,0.3)', color: '#00E5C3', opacity: chatBusy ? 0.5 : 1 }}>{c}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center', borderRadius: 999, padding: 3, backgroundImage: `linear-gradient(rgba(7,3,19,0.85),rgba(7,3,19,0.85)), ${SPEC}`, backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', border: '1.5px solid transparent' }}>
          <input data-testid="studio-chat-input" placeholder={lockedTpl ? '🔒 Locked template — content can\'t be edited' : 'Tell me what to change…'} value={chatInput} disabled={lockedTpl}
            onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') sendChat(); }}
            disabled={chatBusy} style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: TXT, fontFamily: 'Inter', fontSize: 13, padding: '8px 14px' }} />
          <button data-testid="studio-chat-send" onClick={() => sendChat()} disabled={chatBusy || !chatInput.trim()}
            style={{ ...btn(SPEC), width: 36, height: 36, padding: 0, borderRadius: 999, boxShadow: '0 0 14px rgba(255,46,154,0.5)', opacity: chatBusy || !chatInput.trim() ? 0.5 : 1 }}>↑</button>
        </div>
      </div>
    </div>
  );

  const historyView = (
    <div className="studio-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0, overscrollBehavior: 'contain', padding: '10px 26px' }} data-testid="studio-history">
      {linkedLead && (
        <div style={{ ...glass(20), maxWidth: 620, margin: '20px auto', padding: 20 }}>
          <p style={{ fontFamily: HEAD, fontSize: 15, fontWeight: 700, color: TXT, margin: '0 0 12px' }}>Send history{sendForm.first_name ? ` — ${sendForm.first_name}` : ''}</p>
          {leadSends.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 12.5, color: SUB }}>No sends yet for this lead.</p>}
          {leadSends.map((s) => (
            <p key={s.send_id} style={{ fontFamily: 'Inter', fontSize: 12, color: SUB, margin: '8px 0', lineHeight: 1.5 }}>
              <NeonStatus state={sendStatus(s)} pinned testId={`studio-history-status-${s.send_id}`} />
              {' — '}{s.subject || s.campaign_id} <span style={{ opacity: 0.6 }}>· {(s.sent_at || '').slice(0, 16).replace('T', ' ')}</span>
            </p>
          ))}
        </div>
      )}
      <div style={{ ...glass(20), maxWidth: 620, margin: '20px auto', padding: 20 }} data-testid="studio-history-all">
        <p style={{ fontFamily: HEAD, fontSize: 15, fontWeight: 700, color: TXT, margin: '0 0 12px' }}>All recent sends</p>
        {allSends.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 12.5, color: SUB }}>No sends recorded yet.</p>}
        {allSends.map((s) => (
          <p key={s.send_id} style={{ fontFamily: 'Inter', fontSize: 12, color: SUB, margin: '8px 0', lineHeight: 1.5 }}>
            <NeonStatus state={sendStatus(s)} pinned testId={`studio-history-all-${s.send_id}`} />
            {' — '}{s.subject || s.campaign_id} <span style={{ color: TXT, fontWeight: 600 }}>→ {s.email}</span>
            <span style={{ opacity: 0.6 }}> · {(s.sent_at || s.queued_at || '').slice(0, 16).replace('T', ' ')}</span>
            {!s.lead_id && (
              <>
                <span style={{ color: '#F0C36D', fontWeight: 700 }}> · no lead linked</span>
                <button data-testid={`create-lead-from-send-${s.send_id}`}
                  onClick={() => createLeadFromSend(s.send_id)}
                  style={{ ...mini, marginLeft: 8, padding: '1px 9px', fontSize: 10, color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)' }}>＋ Create lead</button>
              </>
            )}
          </p>
        ))}
        {blockedAttempts.length > 0 && (
          <>
            <p style={{ fontFamily: 'Inter', fontSize: 10.5, fontWeight: 800, color: SUB, margin: '16px 0 6px', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Recent blocked attempts</p>
            {blockedAttempts.map((a, i) => (
              <p key={i} style={{ fontFamily: 'Inter', fontSize: 11.5, color: '#FF7DB3', margin: '4px 0' }}>
                ⛔ {a.reason} <span style={{ color: SUB }}>· {a.to_email || 'no recipient'} · {(a.ts || '').slice(0, 16).replace('T', ' ')}</span>
              </p>
            ))}
          </>
        )}
      </div>
    </div>
  );

  return createPortal(
    <div className="km-studio" style={{ position: 'fixed', inset: 0, height: '100dvh', overflow: 'hidden', zIndex: 1000, display: 'flex', flexDirection: 'column', background: BG }} data-testid="email-composer">
      <Aurora />
      <style>{`
        @keyframes tdot { 0%,100%{opacity:.25} 50%{opacity:1} }
        @keyframes genslide { 0%{background-position:0% 0} 100%{background-position:-200% 0} }
        .genbar { animation: genslide 1.2s linear infinite; }
        .genskel { animation: genslide 1.4s linear infinite; }
        .studio-scroll { scrollbar-width: thin; scrollbar-color: rgba(124,92,246,0.28) transparent; }
        @supports selector(::-webkit-scrollbar) {
          .studio-scroll { scrollbar-width: auto; scrollbar-color: auto; }
          .studio-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
          .studio-scroll::-webkit-scrollbar-track { background: transparent; }
          .studio-scroll::-webkit-scrollbar-corner { background: transparent; }
          .studio-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 999px; }
          .studio-scroll:hover::-webkit-scrollbar-thumb, .studio-scroll:focus-within::-webkit-scrollbar-thumb { background: rgba(124,92,246,0.45); }
          .studio-scroll::-webkit-scrollbar-thumb:hover { background: rgba(124,92,246,0.75); }
        }
        .tdot { animation: tdot 1s infinite; display:inline-block; }
        @keyframes livepulse { 0%,100%{box-shadow:0 0 0 0 rgba(0,229,195,.5)} 50%{box-shadow:0 0 0 5px rgba(0,229,195,0)} }
        .live-dot { animation: livepulse 2s infinite; }
      `}</style>

      {/* TOP BAR — floating glass pill */}
      <div style={{ ...glass(18), margin: '14px 24px 0', padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, flexWrap: 'wrap', position: 'relative', zIndex: 2 }}>
        <span style={{ width: 26, height: 26, borderRadius: 8, background: SPEC, boxShadow: '0 0 14px rgba(196,72,240,0.5)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: HEAD, fontSize: 13, fontWeight: 800 }}>K</span>
        <p data-testid="composer-lead-banner" style={{ fontFamily: HEAD, fontSize: 13.5, fontWeight: 700, color: TXT, margin: 0 }}>
          Email Studio {sendForm.first_name && <span style={{ color: SUB, fontWeight: 500 }}>· Draft to {sendForm.first_name}</span>}
        </p>
        <div style={{ display: 'flex', gap: 2, margin: '0 auto', ...glass(999), padding: 3, boxShadow: 'none' }}>
          {['compose', 'preview', 'history'].map((m) => (
            <button key={m} data-testid={`studio-mode-${m}`} onClick={() => { setMode(m); setSelIdx(null); }}
              style={{ ...mini, border: 'none', padding: '5px 16px', textTransform: 'capitalize',
                       background: mode === m ? 'linear-gradient(95deg,rgba(56,182,255,0.35),rgba(196,72,240,0.35))' : 'transparent', color: mode === m ? TXT : SUB }}>{m}</button>
          ))}
        </div>
        {templates.length > 0 && (
          <select onChange={(e) => { const t = templates.find((x) => x.template_id === e.target.value); if (t) loadTemplate(t); e.target.value = ''; }}
            defaultValue="" data-testid="studio-template-select" style={{ ...inputS, width: 'auto', padding: '6px 9px', borderRadius: 999 }}>
            <option value="">Templates…</option>
            {templates.filter((t) => t.category === 'Opener').length > 0 && (
              <optgroup label={`★ Openers · ${templates.filter((t) => t.category === 'Opener').length}`}>
                {templates.filter((t) => t.category === 'Opener').map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
              </optgroup>
            )}
            <optgroup label="My templates">
              {templates.filter((t) => t.category !== 'Opener').map((t) => <option key={t.template_id} value={t.template_id}>{t.locked ? '🔒 ' : ''}{t.name}</option>)}
            </optgroup>
          </select>
        )}
        <button onClick={saveTemplate} disabled={!blocks.length} style={{ ...mini, opacity: blocks.length ? 1 : 0.35 }} data-testid="composer-save-template">Save</button>
        <select value={attachCampaign} onChange={(e) => setAttachCampaign(e.target.value)} data-testid="composer-attach-select" style={{ ...inputS, width: 'auto', padding: '6px 9px', borderRadius: 999 }}>
          <option value="">Attach…</option>
          {(campaigns || []).filter((c) => c.status === 'draft').map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
        </select>
        {attachCampaign && <button onClick={attachToCampaign} disabled={!blocks.length || busy} style={mini} data-testid="composer-attach-btn">Attach</button>}
        <span style={{ width: 26, height: 26, borderRadius: '50%', padding: 2, background: SPEC, display: 'inline-flex' }}>
          <span style={{ flex: 1, borderRadius: '50%', background: '#1A1440', color: TXT, fontFamily: HEAD, fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>MJ</span>
        </span>
        <button onClick={openSendModal}
          style={{ ...btn(SPEC), boxShadow: '0 0 20px rgba(255,46,154,0.45)', opacity: blocks.length && subject ? 1 : 0.35 }} data-testid="composer-send-oneoff">Send ↗</button>
        <button onClick={() => { const next = !motionOn; setMotionEnabled(next); setMotionOn(next); }} title={`Studio motion: ${motionOn ? 'on' : 'off'} — toggles every animation centrally`}
          style={{ ...mini, padding: '7px 10px', color: motionOn ? '#00E5C3' : SUB, border: `1px solid ${motionOn ? 'rgba(0,229,195,0.4)' : 'rgba(255,255,255,0.18)'}` }} data-testid="studio-motion-toggle">{motionOn ? '✦ Motion' : '✦ Still'}</button>
        <button onClick={onClose} style={{ ...mini, padding: '7px 10px' }} data-testid="composer-close"><X size={14} style={{ verticalAlign: -3 }} /></button>
      </div>

      {/* BODY — floating islands, 24px gutters */}
      <V2IconPicker open={pickerOpen} onClose={() => { setPickerOpen(false); setPickerTarget(null); }}
        onPick={handleIconPick}
        title={pickerTarget ? 'Swap — pick a replacement' : (selIdx !== null ? 'Pick an icon — it attaches to the selected block' : 'Icon library')} />
      {library && (
        wide ? (
          <div style={{ flex: 1, display: 'flex', gap: 24, minHeight: 0, overflow: 'hidden', padding: '18px 24px 24px', position: 'relative', zIndex: 1 }}>
            {mode === 'compose' && gallery}
            {mode === 'history' ? historyView : canvas}
            {mode === 'compose' && chatPanel}
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative', overflow: 'hidden', padding: '12px 10px 0', zIndex: 1 }}>
            {mobilePanel === 'rail' && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 30, background: BG, display: 'flex', padding: 10 }}>
                {gallery}
                <div style={{ flex: 1 }} onClick={() => setMobilePanel(null)} />
              </div>
            )}
            {mobilePanel === 'chat' && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 30, background: BG, display: 'flex', flexDirection: 'column', padding: 10 }}>
                <button onClick={() => setMobilePanel(null)} style={{ ...mini, margin: '0 0 8px', alignSelf: 'flex-start' }} data-testid="studio-mobile-chat-close">← Back to email</button>
                <div style={{ flex: 1, minHeight: 0 }}>{chatPanel}</div>
              </div>
            )}
            {mode === 'history' ? historyView : canvas}
            <div style={{ position: 'fixed', bottom: 14, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 10, zIndex: 40, pointerEvents: 'none' }}>
              <button onClick={() => setMobilePanel(mobilePanel === 'rail' ? null : 'rail')} style={{ ...btn('rgba(255,255,255,0.08)', TXT), border: '1px solid rgba(255,255,255,0.15)', pointerEvents: 'auto' }} data-testid="studio-mobile-rail-btn">＋ Blocks</button>
              <button onClick={() => setMobilePanel(mobilePanel === 'chat' ? null : 'chat')} style={{ ...btn(SPEC), pointerEvents: 'auto' }} data-testid="studio-mobile-chat-btn">✦ AI chat</button>
            </div>
          </div>
        )
      )}

      {/* send modal — unchanged gated path */}
      {sendOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,3,19,0.85)', backdropFilter: 'blur(6px)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} data-testid="composer-send-modal">
          <div style={{ ...glass(20), padding: 26, maxWidth: 460, width: '100%', background: 'rgba(20,10,50,0.92)', position: 'relative' }}>
            {sendOk && <span className="km-sweep-bar" data-testid="send-success-sweep" />}
            <p style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 18, color: TXT, margin: '0 0 6px' }}>Send this email — one-off</p>
            <p style={{ fontFamily: 'Inter', fontSize: 12, color: SUB, margin: '0 0 14px' }}>Real send through Resend. Suppression + unsubscribe + warm-up pacing respected, message logged.</p>
            <div style={{ display: 'grid', gap: 8 }}>
              <input data-testid="composer-send-email" placeholder="recipient@example.com" value={sendForm.to_email} onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })} style={inputS} />
              <input data-testid="composer-send-firstname" placeholder="First name" value={sendForm.first_name} onChange={(e) => setSendForm({ ...sendForm, first_name: e.target.value })} style={inputS} />
              <input data-testid="composer-send-business" placeholder="Business name" value={sendForm.business_name} onChange={(e) => setSendForm({ ...sendForm, business_name: e.target.value })} style={inputS} />
            </div>
            <p data-testid="composer-sending-to" style={{ fontFamily: 'Inter', fontSize: 12, margin: '10px 0 0', color: sendForm.to_email.includes('@') ? '#00E5C3' : '#FF2E9A', fontWeight: 700 }}>
              {sendForm.to_email.includes('@') ? `Sending to: ${sendForm.to_email}` : '⛔ No recipient email — fill the To field above'}
            </p>
            {linkedLead?.email && sendForm.to_email.includes('@') && linkedLead.email.toLowerCase().trim() !== sendForm.to_email.toLowerCase().trim() && (
              <p data-testid="composer-recipient-mismatch" style={{ fontFamily: 'Inter', fontSize: 11, margin: '4px 0 0', color: '#F0C36D', fontWeight: 700 }}>
                ⚠ This isn't {linkedLead.first_name || 'the linked lead'}'s address ({linkedLead.email}) — double-check before sending.
              </p>
            )}
            <div data-testid="presend-lint" style={{ margin: '14px 0 0', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', padding: '10px 12px', maxHeight: 280, overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>Pre-send check — curator + PR</p>
                <button data-testid="lint-recheck" onClick={runLint} disabled={lintBusy} style={{ ...mini, marginLeft: 'auto', padding: '1px 8px', fontSize: 10 }}>↻ re-check</button>
              </div>
              {lintBusy && <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: 0 }}>Checking…</p>}
              {!lintBusy && LINT_SECTIONS.map(([sec, secLabel]) => {
                const items = (lint?.checks || []).filter((c) => (c.section || 'delivery') === sec);
                if (!items.length) return null;
                const allPass = items.every((c) => c.status === 'pass');
                return (
                  <div key={sec} data-testid={`lint-section-${sec}`}>
                    <p style={{ margin: '8px 0 2px', fontFamily: 'Inter', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: sec === 'design' ? '#C448F0' : sec === 'pr' ? '#38B6FF' : SUB }}>
                      {secLabel}{allPass ? ' — all clear' : ''}
                    </p>
                    {items.map((c) => (
                      <div key={c.id} data-testid={`lint-${c.id}`} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', margin: '4px 0' }}>
                        <span style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 800, width: 12, flexShrink: 0, color: c.status === 'pass' ? '#00E5C3' : c.status === 'warn' ? '#F0C36D' : '#FF2E9A' }}>{c.status === 'pass' ? '✓' : c.status === 'warn' ? '⚠' : '✕'}</span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 600, color: TXT }}>{c.label}</span>
                          <span style={{ display: 'block', fontFamily: 'Inter', fontSize: 10.5, color: SUB }}>{c.detail}</span>
                          {c.fixable && (
                            <button data-testid={`lint-fix-${c.id}`} onClick={() => { setSendOpen(false); sendChat(c.fix_instruction); }}
                              style={{ ...mini, marginTop: 3, padding: '1px 9px', fontSize: 10, color: '#00E5C3', border: '1px solid rgba(0,229,195,0.4)' }}>✦ Fix it</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
              {!lintBusy && lint?.blocking && <p data-testid="lint-blocking-note" style={{ fontFamily: 'Inter', fontSize: 10.5, color: '#FF2E9A', margin: '6px 0 0', fontWeight: 700 }}>Fix the ✕ items above before sending.</p>}
              {!lintBusy && !lint?.blocking && (lint?.checks || []).some((c) => c.status === 'warn') && (
                <p data-testid="lint-advisory-note" style={{ fontFamily: 'Inter', fontSize: 10.5, color: '#F0C36D', margin: '6px 0 0' }}>⚠ items are advisory — your call, you're the final curator.</p>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setSendOpen(false)} style={mini} data-testid="composer-send-cancel">Cancel</button>
              <button onClick={trySend} className={sendOk ? 'km-sent-ok' : ''}
                style={{ ...btn(sendOk ? '#00B894' : SPEC), opacity: sendOk || (sendForm.to_email.includes('@') && !lintBusy && !lint?.blocking && !busy) ? 1 : 0.5 }} data-testid="composer-send-confirm">{sendOk ? '✓ Sent' : busy ? 'Sending…' : '✉ Send it'}</button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
